import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  message,
  Spin,
  Alert,
  Checkbox,
  Row,
  Col,
  Statistic,
  Tabs,
  Empty,
  Tooltip,
  Modal,
  Breadcrumb,
  Select,
  Input,
  Switch,
  Descriptions,
  Divider,
  Segmented,
  Progress,
  Pagination,
} from "antd";
import {
  ThunderboltOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ApiOutlined,
  DatabaseFilled,
  CloudOutlined,
  FileTextOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SyncOutlined,
  CheckOutlined,
  LinkOutlined,
  CloudServerOutlined,
  LineChartOutlined,
  BranchesOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { pipelineDiscoveryService } from "../services/pipelineDiscoveryService";
import PipelineAlertsTab from "../components/PipelineAlertsTab";
import {
  apiLogItemsToPathGroups,
  applyPipelineDiscoveryPatch,
  collectLogPathsForSelectedPipelines,
  countSelectedPipelineLogAssociations,
  countLogPathTableStats,
  dedupePipelineGroups,
  dedupePipelinesByStableKey,
  getPipelineFromDiscovery,
  getPipelineLogDisplayCounts,
  getPipelineMetricsDisplayCounts,
  getPipelineListForSelectionKey,
  matchPipelineRecord,
  mergeLogPathsWithApi,
  buildPipelineLogsPageParams,
  getPipelineSyncCounts,
  mergeMetricsOnboardedFlags,
  sanitizeDiscoveryMetricFlags,
  metricSelectionKey,
  normalizeMetricsFromApi,
  parsePipelineIdFields,
  pipelineResourceId,
  pipelineSupportsMetricsPage,
  resolvePipelineLogPaths,
  resolvePipelineMetrics,
  getAirflowEnvironmentName,
  getAirflowDagId,
  isAirflowPipeline,
  normalizeAirflowPipelineItem,
  MWAA_METRIC_NAMESPACE_OPTIONS,
  mwaaNamespaceLabel,
  filterMetricsByMwaaNamespace,
  metricsOnboardSummaryByNamespace,
  groupMetricsByNamespace,
} from "../utils/pipelineDiscoveryHelpers";
import {
  formatEmrDateTime,
  formatEmrElapsedTime,
  isEmrPipeline,
} from "../utils/emrStatusHelpers";
import PipelineRuntimeStatus from "../components/PipelineRuntimeStatus";
import {
  hasPipelineRuntimeStatus,
  isGluePipeline,
} from "../utils/pipelineStatusHelpers";
import { accountService } from "../services/accountService";
import { databricksPipelinesService } from "../services/databricksPipelinesService";
import { databricksService } from "../services/databricksService";
import { airflowPipelinesService } from "../services/airflowPipelinesService";
import DatabricksPanel from "../components/pipeline-discovery/DatabricksPanel";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import IsEnabled from "../rolepermission";

const { Option } = Select;

const TAB_KEY_ONBOARDED = "onboarded";
const TAB_KEY_DISCOVERY = "discovery";
const TAB_KEY_ALL = "all";
const ONBOARDING_FILTER_ALL = "all";
const ONBOARDING_FILTER_FULL = "full";
const ONBOARDING_FILTER_PARTIAL = "partial";
const ONBOARDING_FILTER_NONE = "none";
const EMPTY_LINEAGE_VIEW = {
  upstream: [],
  downstream: [],
  paths: [],
  workflowNames: [],
  scannedWorkflows: 0,
  matchedWorkflows: 0,
};

const normalizePipelineType = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!raw) return "";
  if (raw.includes("step")) return "step_functions";
  if (raw.includes("glue")) return "glue";
  if (raw.includes("emr") || raw.includes("elasticmapreduce")) return "emr";
  if (raw.includes("airflow")) return "airflow";
  return raw;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getNodePipelineSummary = (node) => {
  const data = node?.data || {};
  return {
    pipelineId: data.pipelineId || data.pipeline_id || "",
    pipelineName: data.pipelineName || data.pipeline_name || data.label || node?.id || "Pipeline",
    pipelineType: data.pipelineType || data.pipeline_type || data.type || node?.type || "",
    region: data.region || data.pipeline_region || "",
  };
};

const formatPipelineType = (value) => {
  const normalized = normalizePipelineType(value);
  if (normalized === "step_functions") return "Step Functions";
  if (normalized === "glue") return "Glue";
  if (normalized === "emr") return "EMR";
  if (normalized === "airflow") return "Airflow";
  return value || "Unknown";
};

const collectReachableNodeIds = (seedIds, edges, direction) => {
  const visited = new Set(seedIds);
  const reachable = new Set();
  const stack = [...seedIds];

  while (stack.length > 0) {
    const current = stack.pop();
    for (const edge of edges || []) {
      const nextNodeId =
        direction === "downstream"
          ? edge?.source === current
            ? edge?.target
            : null
          : edge?.target === current
            ? edge?.source
            : null;
      if (!nextNodeId || visited.has(nextNodeId)) continue;
      visited.add(nextNodeId);
      reachable.add(nextNodeId);
      stack.push(nextNodeId);
    }
  }

  return reachable;
};

/** Walk upstream edge-by-edge to build an ordered chain ending at targetNodeId. */
const buildLineagePathForNode = (targetNodeId, nodeById, edges) => {
  const steps = [];
  const visited = new Set();
  let current = targetNodeId;

  while (current && !visited.has(current)) {
    visited.add(current);
    const node = nodeById.get(current);
    if (!node) break;
    steps.unshift(getNodePipelineSummary(node));

    const incoming = (edges || []).filter((edge) => edge?.target === current);
    if (incoming.length === 0) break;
    current = incoming[0]?.source || null;
  }

  return steps;
};

const normalizeOnboardingStatus = (value) => {
  const normalized = normalizeText(value);
  if (["full", "fully", "fully_onboarded", "enabled"].includes(normalized)) {
    return "full";
  }
  if (["partial", "partially_onboarded", "partially_enabled"].includes(normalized)) {
    return "partial";
  }
  if (["none", "not_onboarded", "disabled", "not_enabled"].includes(normalized)) {
    return "none";
  }
  return "";
};

const getPipelineLogItems = (pipeline) => [
  ...(pipeline?.log_paths?.cloudwatch || []),
  ...(pipeline?.log_paths?.s3 || []),
];

const isItemOnboarded = (item) =>
  typeof item === "object" ? Boolean(item?.onboarded) : false;

const getPipelineOnboardedCounts = (pipeline, discoveryResults) => {
  const logCounts = getPipelineLogDisplayCounts(pipeline);
  const metricCounts = getPipelineMetricsDisplayCounts(pipeline, discoveryResults);
  return {
    logs: logCounts.onboarded,
    logsTotal: logCounts.total,
    metrics: metricCounts.onboarded,
    metricsTotal: metricCounts.total,
  };
};

export const getPipelineOnboardingState = (pipeline, discoveryResults = null) => {
  const counts = getPipelineOnboardedCounts(pipeline, discoveryResults);
  const supportsMetrics = pipelineSupportsMetricsPage(pipeline);
  const hasLogs = counts.logsTotal > 0;
  const hasMetrics = counts.metricsTotal > 0;

  const logsComplete = !hasLogs || counts.logs >= counts.logsTotal;
  const metricsComplete = supportsMetrics
    ? hasMetrics && counts.metrics >= counts.metricsTotal
    : !hasMetrics || counts.metrics >= counts.metricsTotal;

  if (hasLogs || hasMetrics || supportsMetrics) {
    if (logsComplete && metricsComplete && (counts.logs > 0 || counts.metrics > 0)) {
      return "full";
    }
    if (counts.logs > 0 || counts.metrics > 0) return "partial";
    return "none";
  }

  const explicitStatus =
    normalizeOnboardingStatus(pipeline?.onboarding_status) ||
    normalizeOnboardingStatus(pipeline?.onboardingStatus) ||
    normalizeOnboardingStatus(pipeline?.monitoring_status) ||
    normalizeOnboardingStatus(pipeline?.monitoringStatus) ||
    (pipeline?._fromApi ? normalizeOnboardingStatus(pipeline?.status) : "");

  if (explicitStatus) return explicitStatus;

  if (counts.logs > 0 && counts.metrics > 0) return "full";
  if (counts.logs > 0 || counts.metrics > 0) return "partial";
  return "none";
};

const hasPipelineNewItems = (pipeline, discoveryResults = null) => {
  if (pipeline?._fromApi) {
    return getPipelineOnboardingState(pipeline, discoveryResults) !== "full";
  }

  const hasNewLogs = getPipelineLogItems(pipeline).some(
    (item) => !isItemOnboarded(item),
  );
  const hasNewMetrics = resolvePipelineMetrics(pipeline, discoveryResults).some(
    (metric) => !metric?.onboarded,
  );
  return hasNewLogs || hasNewMetrics;
};

const flattenPipelineGroups = (groups = {}) => [
  ...(groups.glue_jobs || []),
  ...(groups.step_functions || []),
  ...(groups.emr_clusters || []),
  ...(groups.airflow_dags || []),
];

export const getPipelineOnboardingBreakdown = (groups = {}, discoveryResults = null) => {
  const pipelines = flattenPipelineGroups(groups);
  return pipelines.reduce(
    (acc, pipeline) => {
      acc.total += 1;
      const state = getPipelineOnboardingState(pipeline, discoveryResults);
      if (state === "full") acc.fullyOnboarded += 1;
      else if (state === "partial") acc.partiallyOnboarded += 1;
      else acc.notOnboarded += 1;
      return acc;
    },
    {
      total: 0,
      fullyOnboarded: 0,
      partiallyOnboarded: 0,
      notOnboarded: 0,
    },
  );
};

const PipelineOnboardingProgress = ({
  icon: Icon,
  iconColor,
  onboarded,
  total,
  activeColor,
  completeColor = "#10b981",
  isDark = false,
  emptyLabel,
  kind = "metrics",
}) => {
  const kindClass =
    kind === "logs" ? "pipeline-discovery-progress--logs" : "pipeline-discovery-progress--metrics";

  if (total <= 0) {
    return (
      <div
        className={`pipeline-discovery-progress pipeline-discovery-progress--empty ${kindClass}`}
        data-theme={isDark ? "dark" : "light"}
      >
        <Icon style={{ color: iconColor, fontSize: 14, flexShrink: 0 }} />
        <span className="pipeline-discovery-progress-empty-label">{emptyLabel}</span>
      </div>
    );
  }

  const percent = Math.round((onboarded / total) * 100);
  const isComplete = onboarded === total;

  return (
    <div
      className={`pipeline-discovery-progress ${kindClass}`}
      data-theme={isDark ? "dark" : "light"}
      style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
    >
      <Icon style={{ color: iconColor, fontSize: 14, flexShrink: 0 }} />
      <Progress
        percent={percent}
        size="small"
        strokeColor={isComplete ? completeColor : activeColor}
        trailColor={isDark ? "#30363d" : "#e5e7eb"}
        strokeWidth={8}
        format={() =>
          isComplete ? (
            <CheckOutlined style={{ color: completeColor, fontSize: 12 }} />
          ) : (
            <span className="pipeline-discovery-progress-count">{onboarded}/{total}</span>
          )
        }
        style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
      />
    </div>
  );
};

const PipelineDiscoveryPage = () => {
  const { user, accounts, setAccounts } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isViewer = !IsEnabled("Accounts");

  // Pipeline status tab: onboarded | discovery | all (persisted in URL)
  const activePipelineTab = searchParams.get("tab") || TAB_KEY_ALL;
  const setActivePipelineTab = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === TAB_KEY_ALL) next.delete("tab");
      else next.set("tab", key);
      return next;
    });
  };

  // Summary-card filter: all | full | partial | none (persisted in URL)
  const onboardingFilter = searchParams.get("onboarding") || ONBOARDING_FILTER_ALL;
  const setOnboardingFilter = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value || value === ONBOARDING_FILTER_ALL) next.delete("onboarding");
      else next.set("onboarding", value);
      return next;
    });
  };

  const selectSummaryCardFilter = (filter) => {
    setOnboardingFilter(filter);
    setActivePipelineTab(TAB_KEY_ALL);
  };

  // State management
  const [loading, setLoading] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [discoveryResults, setDiscoveryResults] = useState(null);
  const [selectedPipelines, setSelectedPipelines] = useState({
    glue: new Set(),
    stepfunctions: new Set(),
    emr: new Set(),
    airflow: new Set(),
  });
  const [selectedLogPaths, setSelectedLogPaths] = useState({
    cloudwatch: new Set(),
    s3: new Set(),
  });
  const [selectedMetrics, setSelectedMetrics] = useState(new Set()); // Store metric keys
  const [modalSelectedMetricKeys, setModalSelectedMetricKeys] = useState(
    () => new Set(),
  );
  const [modalMwaaNamespaceFilter, setModalMwaaNamespaceFilter] = useState("all");
  const [onboardingModalMetrics, setOnboardingModalMetrics] = useState(false);
  const [logPathFilter, setLogPathFilter] = useState("all"); // 'all', 'new', 'onboarded'

  // Filter persistence in URL (filterType, search) - survives tab switch and refresh
  const searchQuery = searchParams.get("search") || "";
  const filterType = searchParams.get("filterType") || "all";
  const setSearchQuery = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val == null || val === "") next.delete("search");
      else next.set("search", val);
      return next;
    });
  };
  const setFilterType = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val == null || val === "all") next.delete("filterType");
      else next.set("filterType", val);
      return next;
    });
  };
  const clearAllFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("search");
      next.delete("filterType");
      return next;
    });
    setLogPathFilter("all");
  };
  const hasActiveFilters =
    filterType !== "all" || searchQuery || logPathFilter !== "all";
  const [onboardingResults, setOnboardingResults] = useState(null);
  const [showOnboardingResultsModal, setShowOnboardingResultsModal] =
    useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [syncingPipelineId, setSyncingPipelineId] = useState(null);
  const [pipelineDetailModalOpen, setPipelineDetailModalOpen] = useState(false);
  const [selectedPipelineForModal, setSelectedPipelineForModal] =
    useState(null);
  const [pipelineDetailLoading, setPipelineDetailLoading] = useState(false);
  const [lineageEnabled, setLineageEnabled] = useState(true);
  const [lineageSaving, setLineageSaving] = useState(false);
  const [lineageViewLoading, setLineageViewLoading] = useState(false);
  const [lineageViewError, setLineageViewError] = useState("");
  const [lineageView, setLineageView] = useState(EMPTY_LINEAGE_VIEW);
  const [pipelinePage, setPipelinePage] = useState({
    glue: 1,
    stepfunctions: 1,
    emr: 1,
    airflow: 1,
  });
  const [activeAwsContentTab, setActiveAwsContentTab] = useState("pipelines");
  const [activePipelineDetailTab, setActivePipelineDetailTab] = useState("logs");
  const [pipelineAlertsOnly, setPipelineAlertsOnly] = useState(false);
  const [grafanaLinkUrl, setGrafanaLinkUrl] = useState(null);
  const [grafanaLinkLoading, setGrafanaLinkLoading] = useState(false);
  const [activePipelineTypeTab, setActivePipelineTypeTab] = useState("glue");
  const TABLE_SCROLL_Y = 420;
  const logPathsTableWrapRef = useRef(null);
  const [logPathsScrollY, setLogPathsScrollY] = useState(360);

  // Airflow (MWAA) state
  const [airflowEnvironments, setAirflowEnvironments] = useState([]);
  const [selectedAirflowEnvironment, setSelectedAirflowEnvironment] = useState(null);
  const [airflowPipelines, setAirflowPipelines] = useState([]);
  const [airflowPipelinesLoading, setAirflowPipelinesLoading] = useState(false);
  const [airflowEnvironmentsLoading, setAirflowEnvironmentsLoading] = useState(false);
  const [airflowPipelineToggling, setAirflowPipelineToggling] = useState(null);
  const PIPELINE_PAGE_SIZE = 15;
  const LOG_PATH_PAGE_SIZE = 20;
  const [logPathsPage, setLogPathsPage] = useState(1);
  const [logPathsPageSize, setLogPathsPageSize] = useState(LOG_PATH_PAGE_SIZE);

  // Source tabs: AWS | Databricks (like Logs/Metrics)
  const [activeSourceTab, setActiveSourceTab] = useState("aws");
  // Databricks pipelines state
  const [databricksAccounts, setDatabricksAccounts] = useState([]);
  const [selectedDatabricksAccount, setSelectedDatabricksAccount] = useState(null);
  const [databricksWorkspaces, setDatabricksWorkspaces] = useState([]);
  const [selectedDatabricksWorkspace, setSelectedDatabricksWorkspace] = useState(null);
  const [databricksPipelines, setDatabricksPipelines] = useState([]);
  const [databricksPipelinesLoading, setDatabricksPipelinesLoading] = useState(false);
  const [databricksAccountsLoading, setDatabricksAccountsLoading] = useState(false);
  const [databricksPipelineToggling, setDatabricksPipelineToggling] = useState(null);
  const [databricksAwsAccountId, setDatabricksAwsAccountId] = useState(null);

  // Reset pipeline pagination when status tab changes
  useEffect(() => {
    setPipelinePage({ glue: 1, stepfunctions: 1, emr: 1, airflow: 1 });
  }, [activePipelineTab]);

  // Sync pipeline type tab (Glue | Step Functions | EMR | Airflow) with filter dropdown selection
  useEffect(() => {
    if (filterType === "glue") setActivePipelineTypeTab("glue");
    else if (filterType === "step_functions")
      setActivePipelineTypeTab("stepfunctions");
    else if (filterType === "emr") setActivePipelineTypeTab("emr");
    else if (filterType === "airflow") setActivePipelineTypeTab("airflow");
    else if (filterType === "all") setActivePipelineTypeTab("glue");
  }, [filterType]);

  // Status & Onboarded APIs (fast, DynamoDB-only) - loaded when account selected
  const [statusSummary, setStatusSummary] = useState(null);
  const [onboardedPipelinesFromApi, setOnboardedPipelinesFromApi] =
    useState(null);
  const [statusApiLoading, setStatusApiLoading] = useState(false);
  const [statusApiError, setStatusApiError] = useState("");

  // When switching to Discovery tab, default filter to New Only (once per switch)
  const prevPipelineTabRef = React.useRef(activePipelineTab);
  useEffect(() => {
    if (
      prevPipelineTabRef.current !== TAB_KEY_DISCOVERY &&
      activePipelineTab === TAB_KEY_DISCOVERY
    ) {
      setLogPathFilter("new");
    }
    prevPipelineTabRef.current = activePipelineTab;
  }, [activePipelineTab]);

  useEffect(() => {
    if (activeSourceTab !== "aws") {
      setActiveAwsContentTab("pipelines");
    }
  }, [activeSourceTab]);

  // Fetch accounts if not loaded
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user?.client_id) {
        return;
      }

      // Only fetch if accounts are empty
      if (accounts.length === 0 && !accountsLoading) {
        setAccountsLoading(true);
        try {
          const allAccounts = await accountService.getAccounts();
          const mappedDataArray = [];

          // Transform the data structure: { clientId: { accountId: accountData } }
          for (const clientId in allAccounts) {
            for (const accountId in allAccounts[clientId]) {
              mappedDataArray.push({
                ...allAccounts[clientId][accountId],
                accountId: accountId,
                clientId: clientId,
              });
            }
          }

          if (setAccounts) {
            setAccounts(mappedDataArray);
          }
        } catch (error) {
          console.error("Error fetching accounts:", error);
          message.error("Failed to load AWS accounts");
        } finally {
          setAccountsLoading(false);
        }
      }
    };

    fetchAccounts();
  }, [user?.client_id, accounts.length, accountsLoading, setAccounts]);

  // Fetch Databricks accounts when Databricks tab is active
  useEffect(() => {
    if (activeSourceTab !== "databricks") return;
    const fetch = async () => {
      setDatabricksAccountsLoading(true);
      try {
        const list = await databricksService.getAccounts();
        setDatabricksAccounts(list || []);
      } catch (err) {
        console.error("Error fetching Databricks accounts:", err);
        message.error("Failed to load Databricks accounts");
        setDatabricksAccounts([]);
      } finally {
        setDatabricksAccountsLoading(false);
      }
    };
    fetch();
  }, [activeSourceTab]);

  // Fetch Databricks workspaces when account selected
  useEffect(() => {
    if (activeSourceTab !== "databricks" || !selectedDatabricksAccount?.account_id) return;
    const fetch = async () => {
      try {
        const workspaces = await databricksService.getWorkspaces(selectedDatabricksAccount.account_id);
        setDatabricksWorkspaces(workspaces || []);
        setSelectedDatabricksWorkspace(null);
        setDatabricksPipelines([]);
      } catch (err) {
        console.error("Error fetching Databricks workspaces:", err);
        setDatabricksWorkspaces([]);
      }
    };
    fetch();
  }, [activeSourceTab, selectedDatabricksAccount?.account_id]);

  // Fetch Airflow (MWAA) environments when AWS account selected
  useEffect(() => {
    if (activeSourceTab !== "aws" || !selectedAccount?.accountId) return;
    setAirflowEnvironmentsLoading(true);
    airflowPipelinesService
      .getEnvironments(
        selectedAccount.accountId,
        selectedAccount.clientId || user?.client_id,
      )
      .then((res) => {
        const environments = res.environments || [];
        setAirflowEnvironments((prev) => {
          const merged = new Map(prev.map((env) => [env.name, env]));
          environments.forEach((env) => merged.set(env.name, env));
          return Array.from(merged.values());
        });
        setSelectedAirflowEnvironment((prev) => prev || environments[0] || null);
      })
      .catch((err) => {
        console.error("Error fetching Airflow environments:", err);
      })
      .finally(() => setAirflowEnvironmentsLoading(false));
  }, [activeSourceTab, selectedAccount?.accountId, selectedAccount?.clientId, user?.client_id]);

  // Get available AWS accounts
  const awsAccounts = useMemo(() => {
    return accounts.filter(
      (acc) => acc.accountId && acc.status === "completed",
    );
  }, [accounts]);

  // Auto-select first AWS account for Databricks when tab active
  useEffect(() => {
    if (activeSourceTab === "databricks" && awsAccounts.length > 0 && !databricksAwsAccountId) {
      setDatabricksAwsAccountId(awsAccounts[0].accountId);
    }
  }, [activeSourceTab, awsAccounts, databricksAwsAccountId]);

  // Filter pipelines based on search and type (used for log paths, bulk select, etc.)
  const filteredPipelines = useMemo(() => {
    if (!discoveryResults)
      return { glue_jobs: [], step_functions: [], emr_clusters: [], airflow_dags: [] };

    const allPipelines = {
      glue_jobs: discoveryResults.pipelines?.glue_jobs || [],
      step_functions: discoveryResults.pipelines?.step_functions || [],
      emr_clusters: discoveryResults.pipelines?.emr_clusters || [],
      airflow_dags: discoveryResults.pipelines?.airflow_dags || [],
    };

    if (filterType === "all" && !searchQuery) return allPipelines;

    const filterPipeline = (pipeline) => {
      const matchesType = filterType === "all" || pipeline.type === filterType;
      const matchesSearch =
        !searchQuery ||
        pipeline.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pipeline.region?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    };

    return {
      glue_jobs: dedupePipelinesByStableKey(
        allPipelines.glue_jobs.filter(filterPipeline),
      ),
      step_functions: dedupePipelinesByStableKey(
        allPipelines.step_functions.filter(filterPipeline),
      ),
      emr_clusters: dedupePipelinesByStableKey(
        allPipelines.emr_clusters.filter(filterPipeline),
      ),
      airflow_dags: dedupePipelinesByStableKey(
        allPipelines.airflow_dags.filter(filterPipeline),
      ),
    };
  }, [discoveryResults, searchQuery, filterType]);

  const discoveryPipelineGroups = useMemo(() => {
    if (!discoveryResults?.pipelines) return null;
    return dedupePipelineGroups(discoveryResults.pipelines);
  }, [discoveryResults]);

  const matchesPipelineSearchQuery = useCallback(
    (pipeline) =>
      !searchQuery ||
      pipeline.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pipeline.region?.toLowerCase().includes(searchQuery.toLowerCase()),
    [searchQuery],
  );

  // Prepare log paths data grouped by pipeline (used in Log Paths tab and onboarding handlers)
  const logPathsData = useMemo(() => {
    if (!discoveryResults) return [];

    // Group log paths by pipeline
    const pipelineGroups = {};

    // Process CloudWatch log paths
    discoveryResults.log_paths?.cloudwatch?.forEach((item) => {
      const path = typeof item === "object" ? item.path : item;
      const onboarded = typeof item === "object" ? item.onboarded : false;
      const pipelineName =
        typeof item === "object" ? item.pipeline_name : "Unknown";
      const pipelineType = typeof item === "object" ? item.pipeline_type : null;
      const pipelineRegion =
        typeof item === "object" ? item.pipeline_region : null;

      const pipelineKey = `${pipelineName}-${pipelineType}-${
        pipelineRegion || ""
      }`;

      if (!pipelineGroups[pipelineKey]) {
        pipelineGroups[pipelineKey] = {
          key: `pipeline-${pipelineKey}`,
          pipelineName,
          pipelineType,
          pipelineRegion,
          logPaths: [],
        };
      }

      pipelineGroups[pipelineKey].logPaths.push({
        key: `cw-${path}-${pipelineName}`,
        type: "cloudwatch",
        path,
        onboarded,
      });
    });

    // Process S3 log paths
    discoveryResults.log_paths?.s3?.forEach((item) => {
      const path = typeof item === "object" ? item.path : item;
      const onboarded = typeof item === "object" ? item.onboarded : false;
      const pipelineName =
        typeof item === "object" ? item.pipeline_name : "Unknown";
      const pipelineType = typeof item === "object" ? item.pipeline_type : null;
      const pipelineRegion =
        typeof item === "object" ? item.pipeline_region : null;

      const pipelineKey = `${pipelineName}-${pipelineType}-${
        pipelineRegion || ""
      }`;

      if (!pipelineGroups[pipelineKey]) {
        pipelineGroups[pipelineKey] = {
          key: `pipeline-${pipelineKey}`,
          pipelineName,
          pipelineType,
          pipelineRegion,
          logPaths: [],
        };
      }

      pipelineGroups[pipelineKey].logPaths.push({
        key: `s3-${path}-${pipelineName}`,
        type: "s3",
        path,
        onboarded,
      });
    });

    // Convert to array and sort by pipeline name
    let groupedData = Object.values(pipelineGroups).sort((a, b) =>
      a.pipelineName.localeCompare(b.pipelineName),
    );

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      groupedData = groupedData
        .filter((group) => {
          // Filter by pipeline name, type, region, or log path
          const matchesPipeline =
            group.pipelineName?.toLowerCase().includes(query) ||
            group.pipelineType?.toLowerCase().includes(query) ||
            group.pipelineRegion?.toLowerCase().includes(query);

          const matchesLogPath = group.logPaths.some((logPath) =>
            logPath.path?.toLowerCase().includes(query),
          );

          return matchesPipeline || matchesLogPath;
        })
        .map((group) => {
          // If search matches log path, filter log paths too
          if (searchQuery) {
            const innerQuery = searchQuery.toLowerCase();
            const filteredLogPaths = group.logPaths.filter(
              (logPath) =>
                logPath.path?.toLowerCase().includes(innerQuery) ||
                group.pipelineName?.toLowerCase().includes(innerQuery) ||
                group.pipelineType?.toLowerCase().includes(innerQuery) ||
                group.pipelineRegion?.toLowerCase().includes(innerQuery),
            );
            // Only include group if it has matching log paths or matches pipeline
            if (
              filteredLogPaths.length > 0 ||
              group.pipelineName?.toLowerCase().includes(innerQuery) ||
              group.pipelineType?.toLowerCase().includes(innerQuery) ||
              group.pipelineRegion?.toLowerCase().includes(innerQuery)
            ) {
              return {
                ...group,
                logPaths:
                  filteredLogPaths.length > 0
                    ? filteredLogPaths
                    : group.logPaths,
              };
            }
            return null;
          }
          return group;
        })
        .filter(Boolean);
    }

    // Apply type filter (if not 'all')
    if (filterType !== "all") {
      groupedData = groupedData.filter((group) => {
        const typeMap = {
          glue: "glue",
          step_functions: "step_functions",
          emr: "emr",
          airflow: "airflow",
        };
        return group.pipelineType === typeMap[filterType];
      });
    }

    // Apply log path status filter
    if (logPathFilter === "new") {
      groupedData = groupedData
        .map((group) => ({
          ...group,
          logPaths: group.logPaths.filter((item) => !item.onboarded),
        }))
        .filter((group) => group.logPaths.length > 0);
    } else if (logPathFilter === "onboarded") {
      groupedData = groupedData
        .map((group) => ({
          ...group,
          logPaths: group.logPaths.filter((item) => item.onboarded),
        }))
        .filter((group) => group.logPaths.length > 0);
    }

    // Match Log Paths tab rows to the active Onboarded / Discovery / All status tab
    if (activePipelineTab !== TAB_KEY_ALL && discoveryResults?.pipelines) {
      const findDiscoveryPipeline = (name, type, region) => {
        const lists = [
          discoveryResults.pipelines.glue_jobs,
          discoveryResults.pipelines.step_functions,
          discoveryResults.pipelines.emr_clusters,
          discoveryResults.pipelines.airflow_dags,
        ];
        for (const list of lists) {
          for (const pipeline of list || []) {
            const pipelineName = pipeline.name || pipeline.pipeline_name;
            const pipelineType = pipeline.type || pipeline.pipeline_type;
            const pipelineRegion = pipeline.region || pipeline.pipeline_region || "";
            if (
              pipelineName === name &&
              pipelineType === type &&
              pipelineRegion === (region || "")
            ) {
              return pipeline;
            }
          }
        }
        return null;
      };

      groupedData = groupedData.filter((group) => {
        const pipeline = findDiscoveryPipeline(
          group.pipelineName,
          group.pipelineType,
          group.pipelineRegion,
        );
        if (!pipeline) return true;

        if (onboardingFilter !== ONBOARDING_FILTER_ALL) {
          const state = getPipelineOnboardingState(pipeline, discoveryResults);
          if (onboardingFilter === ONBOARDING_FILTER_FULL && state !== "full") {
            return false;
          }
          if (onboardingFilter === ONBOARDING_FILTER_PARTIAL && state !== "partial") {
            return false;
          }
          if (onboardingFilter === ONBOARDING_FILTER_NONE && state !== "none") {
            return false;
          }
        }

        if (activePipelineTab === TAB_KEY_ONBOARDED) {
          const state = getPipelineOnboardingState(pipeline, discoveryResults);
          return state === "full" || state === "partial";
        }
        if (activePipelineTab === TAB_KEY_DISCOVERY) {
          return hasPipelineNewItems(pipeline, discoveryResults);
        }
        return true;
      });
    }

    return groupedData;
  }, [
    discoveryResults,
    logPathFilter,
    searchQuery,
    filterType,
    activePipelineTab,
    onboardingFilter,
  ]);

  const logPathTableData = useMemo(
    () =>
      logPathsData.flatMap((group) =>
        (group.logPaths || []).map((logPath, index) => ({
          ...logPath,
          key: `${group.key}-${logPath.key}-${index}`,
          pipelineName: group.pipelineName,
          pipelineType: group.pipelineType,
          pipelineRegion: group.pipelineRegion,
        })),
      ),
    [logPathsData],
  );

  const visibleLogPathStats = useMemo(
    () => countLogPathTableStats(logPathTableData),
    [logPathTableData],
  );

  const paginatedLogPathTableData = useMemo(() => {
    const start = (logPathsPage - 1) * logPathsPageSize;
    return logPathTableData.slice(start, start + logPathsPageSize);
  }, [logPathTableData, logPathsPage, logPathsPageSize]);

  useEffect(() => {
    setLogPathsPage(1);
  }, [
    logPathTableData.length,
    searchQuery,
    filterType,
    logPathFilter,
    selectedAccount,
    activeAwsContentTab,
    activePipelineTab,
  ]);

  useEffect(() => {
    if (activeAwsContentTab !== "logpaths") return undefined;

    const wrap = logPathsTableWrapRef.current;
    if (!wrap) return undefined;

    const updateScrollY = () => {
      const header = wrap.querySelector(".ant-table-header");
      const headerHeight = header?.getBoundingClientRect().height ?? 40;
      const available = wrap.clientHeight - headerHeight - 12;
      setLogPathsScrollY(Math.max(160, available));
    };

    updateScrollY();
    const observer = new ResizeObserver(updateScrollY);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [
    activeAwsContentTab,
    logPathsPage,
    logPathsPageSize,
    logPathTableData.length,
    logPathFilter,
    activePipelineTab,
  ]);

  // Pipelines from onboarded API, transformed to discovery format (when no discovery run)
  // Infer pipeline_type from pipeline_id when missing (format: {type}_{name}_{region})
  const onboardedApiAsDiscovery = useMemo(() => {
    if (!onboardedPipelinesFromApi?.length) return null;
    const inferType = (p) => {
      const t = String(p.pipeline_type || "")
        .toLowerCase()
        .replace(/-/g, "_");
      if (t === "glue" || t === "step_functions" || t === "step_function") {
        return t === "step_function" ? "step_functions" : t;
      }
      if (t === "emr" || t === "airflow") return t;
      const pid = String(p.pipeline_id || "").toLowerCase();
      if (pid.startsWith("glue_")) return "glue";
      if (pid.startsWith("step_functions_")) return "step_functions";
      if (pid.startsWith("emr_")) return "emr";
      if (pid.startsWith("airflow_")) return "airflow";
      return "glue";
    };
    const toPipeline = (p, resolvedType) => {
      const parsed = parsePipelineIdFields(p.pipeline_id);
      const resourceId =
        resolvedType === "emr" && parsed.emrClusterId
          ? parsed.emrClusterId
          : p.pipeline_id;
      return {
        id: resourceId,
        pipeline_id: p.pipeline_id,
        name: p.pipeline_name || parsed.name,
        type: resolvedType,
        region: p.region || parsed.region,
        pipeline_type: resolvedType,
        pipeline_region: p.region || parsed.region,
        log_paths: { cloudwatch: [], s3: [] },
        metrics: [],
        logs_onboarded: p.logs_onboarded ?? 0,
        logs_total: p.logs_total ?? p.logs_onboarded ?? 0,
        metrics_onboarded: p.metrics_onboarded ?? 0,
        metrics_total: p.metrics_total ?? p.metrics_onboarded ?? 0,
        onboarding_status: p.status,
        _fromApi: true,
      };
    };
    const glue = [];
    const step = [];
    const emr = [];
    const airflow = [];
    onboardedPipelinesFromApi
      .filter((p) => p.pipeline_id && (p.pipeline_name || p.pipeline_id))
      .forEach((p) => {
      const resolvedType = inferType(p);
      const pipeline = toPipeline(p, resolvedType);
      const discP = discoveryResults
        ? getPipelineFromDiscovery(discoveryResults, p.pipeline_id)
        : null;
      if (discP) {
        if (discP.log_paths) {
          pipeline.log_paths = discP.log_paths;
          pipeline.id = discP.id || pipeline.id;
          pipeline.name = discP.name || pipeline.name;
        }
        pipeline.dag_id = discP.dag_id || discP.id || pipeline.dag_id || pipeline.id;
        pipeline.environment_name =
          getAirflowEnvironmentName(discP) || pipeline.environment_name;
        pipeline.workspace_name =
          discP.workspace_name || pipeline.workspace_name || pipeline.environment_name;
        pipeline.is_paused = discP.is_paused ?? pipeline.is_paused;
        pipeline.is_active = discP.is_active ?? pipeline.is_active;
        pipeline.status = discP.status || pipeline.status;
        pipeline.status_message = discP.status_message || pipeline.status_message;
        pipeline.started_on = discP.started_on || pipeline.started_on;
        pipeline.completed_on = discP.completed_on || pipeline.completed_on;
        pipeline.last_run_id = discP.last_run_id || pipeline.last_run_id;
        pipeline.execution_arn = discP.execution_arn || pipeline.execution_arn;
        pipeline.state_machine_status =
          discP.state_machine_status || pipeline.state_machine_status;
      }
      pipeline.metrics = resolvePipelineMetrics(discP || pipeline, discoveryResults);
      if (resolvedType === "glue") glue.push(pipeline);
      else if (resolvedType === "step_functions") step.push(pipeline);
      else if (resolvedType === "emr") emr.push(pipeline);
      else if (resolvedType === "airflow") airflow.push(pipeline);
    });
    return {
      glue_jobs: glue,
      step_functions: step,
      emr_clusters: emr,
      airflow_dags: airflow,
    };
  }, [onboardedPipelinesFromApi, discoveryResults, selectedAccount?.accountId]);

  // Derive MWAA environments from discovered/onboarded Airflow DAGs
  useEffect(() => {
    const dags = [
      ...(discoveryResults?.pipelines?.airflow_dags || []),
      ...(onboardedApiAsDiscovery?.airflow_dags || []),
    ];
    if (!dags.length) return;

    const derived = new Map();
    dags.forEach((dag) => {
      const name = getAirflowEnvironmentName(dag);
      if (!name) return;
      derived.set(name, { name, region: dag.region || null });
    });
    if (!derived.size) return;

    setAirflowEnvironments((prev) => {
      const merged = new Map(prev.map((env) => [env.name, env]));
      derived.forEach((env, name) => {
        if (!merged.has(name)) merged.set(name, env);
      });
      return Array.from(merged.values());
    });
    setSelectedAirflowEnvironment((prev) => {
      if (prev?.name || prev) return prev;
      return Array.from(derived.values())[0] || null;
    });
  }, [discoveryResults, onboardedApiAsDiscovery]);

  // Pipeline status: from discovery (when available) or from onboarded API list (Quick view) or status API
  const pipelineStatusCounts = useMemo(() => {
    if (discoveryResults?.pipelines) {
      const allPipelines = flattenPipelineGroups(discoveryResults.pipelines);
      const onboardedCount = allPipelines.filter((pipeline) => {
        const state = getPipelineOnboardingState(pipeline, discoveryResults);
        return state === "full" || state === "partial";
      }).length;
      const discoveryCount = allPipelines.filter((p) =>
        hasPipelineNewItems(p, discoveryResults),
      ).length;
      return {
        onboarded: onboardedCount,
        discovery: discoveryCount,
        all: allPipelines.length,
      };
    }
    if (onboardedApiAsDiscovery) {
      const allPipelines = flattenPipelineGroups(onboardedApiAsDiscovery);
      return {
        onboarded: allPipelines.filter((pipeline) => {
          const state = getPipelineOnboardingState(pipeline, onboardedApiAsDiscovery);
          return state === "full" || state === "partial";
        }).length,
        discovery: allPipelines.filter((p) =>
          hasPipelineNewItems(p, onboardedApiAsDiscovery),
        ).length,
        all: allPipelines.length,
      };
    }
    if (statusSummary) {
      const total = statusSummary.total ?? 0;
      return {
        onboarded: statusSummary.full ?? 0,
        discovery: (statusSummary.partial ?? 0) + (statusSummary.none ?? 0),
        all: total,
      };
    }
    return { onboarded: 0, discovery: 0, all: 0 };
  }, [discoveryResults, onboardedApiAsDiscovery, statusSummary]);

  // Onboarding breakdown: derive from pipeline rows (same logic as table badges).
  const onboardingBreakdown = useMemo(() => {
    if (discoveryResults?.pipelines) {
      return getPipelineOnboardingBreakdown(
        dedupePipelineGroups(discoveryResults.pipelines),
        discoveryResults,
      );
    }
    if (discoveryResults?.summary) {
      const summary = discoveryResults.summary;
      const total =
        summary.total_pipelines ??
        summary.onboarding_total ??
        0;
      return {
        total,
        fullyOnboarded: summary.onboarding_full ?? 0,
        partiallyOnboarded: summary.onboarding_partial ?? 0,
        notOnboarded: summary.onboarding_none ?? 0,
      };
    }
    if (onboardedApiAsDiscovery) {
      return getPipelineOnboardingBreakdown(onboardedApiAsDiscovery);
    }
    if (statusSummary) {
      const total = statusSummary.total ?? 0;
      return {
        total,
        fullyOnboarded: statusSummary.full ?? 0,
        partiallyOnboarded: statusSummary.partial ?? 0,
        notOnboarded: statusSummary.none ?? 0,
      };
    }
    return {
      total: 0,
      fullyOnboarded: 0,
      partiallyOnboarded: 0,
      notOnboarded: 0,
    };
  }, [discoveryResults, onboardedApiAsDiscovery, statusSummary]);

  // Filter pipelines by active status tab - prefer live discovery rows when available
  // so runtime STATUS (Running/Succeeded/etc.) is always populated on every tab.
  // Type dropdown filter applies to the table list only, not badge/tab counts.
  const pipelinesByStatusTab = useMemo(() => {
    const applySearchFilter = (arr) =>
      (arr || []).filter(matchesPipelineSearchQuery);

    const source = discoveryPipelineGroups
      ? {
          glue: applySearchFilter(discoveryPipelineGroups.glue_jobs),
          step: applySearchFilter(discoveryPipelineGroups.step_functions),
          emr: applySearchFilter(discoveryPipelineGroups.emr_clusters),
          airflow: applySearchFilter(discoveryPipelineGroups.airflow_dags),
          fromDiscovery: true,
        }
      : onboardedApiAsDiscovery
        ? {
            glue: applySearchFilter(onboardedApiAsDiscovery.glue_jobs),
            step: applySearchFilter(onboardedApiAsDiscovery.step_functions),
            emr: applySearchFilter(onboardedApiAsDiscovery.emr_clusters),
            airflow: applySearchFilter(onboardedApiAsDiscovery.airflow_dags),
            fromDiscovery: false,
          }
        : null;
    if (!source)
      return {
        glue_jobs: [],
        step_functions: [],
        emr_clusters: [],
        airflow_dags: [],
        glue_originalIndices: [],
        step_originalIndices: [],
        emr_originalIndices: [],
        airflow_originalIndices: [],
      };

    const isPipelineOnboarded = (p) => {
      const state = getPipelineOnboardingState(p, discoveryResults);
      return state === "full" || state === "partial";
    };

    const matchesOnboardingCardFilter = (p) => {
      if (onboardingFilter === ONBOARDING_FILTER_ALL) return true;
      const state = getPipelineOnboardingState(p, discoveryResults);
      if (onboardingFilter === ONBOARDING_FILTER_FULL) return state === "full";
      if (onboardingFilter === ONBOARDING_FILTER_PARTIAL) return state === "partial";
      if (onboardingFilter === ONBOARDING_FILTER_NONE) return state === "none";
      return true;
    };

    const filterWithIndices = (arr, pred) => {
      const filtered = [];
      const indices = [];
      arr.forEach((p, i) => {
        if (pred(p)) {
          filtered.push(p);
          indices.push(i);
        }
      });
      return { list: filtered, indices };
    };

    const tabPred =
      activePipelineTab === TAB_KEY_ALL
        ? () => true
        : activePipelineTab === TAB_KEY_ONBOARDED
          ? isPipelineOnboarded
          : (p) => hasPipelineNewItems(p, discoveryResults);

    const pred = (p) =>
      matchesOnboardingCardFilter(p) && tabPred(p);

    const g = filterWithIndices(source.glue, pred);
    const s = filterWithIndices(source.step, pred);
    const e = filterWithIndices(source.emr, pred);
    const a = filterWithIndices(source.airflow, pred);

    return {
      glue_jobs: g.list,
      glue_originalIndices: g.indices,
      step_functions: s.list,
      step_originalIndices: s.indices,
      emr_clusters: e.list,
      emr_originalIndices: e.indices,
      airflow_dags: a.list,
      airflow_originalIndices: a.indices,
    };
  }, [
    discoveryPipelineGroups,
    onboardedApiAsDiscovery,
    activePipelineTab,
    onboardingFilter,
    discoveryResults,
    matchesPipelineSearchQuery,
  ]);

  const visiblePipelineTotal = useMemo(
    () =>
      (pipelinesByStatusTab.glue_jobs?.length || 0) +
      (pipelinesByStatusTab.step_functions?.length || 0) +
      (pipelinesByStatusTab.emr_clusters?.length || 0) +
      (pipelinesByStatusTab.airflow_dags?.length || 0),
    [pipelinesByStatusTab],
  );

  const visibleAirflowPipelineCount = useMemo(() => {
    const dags = pipelinesByStatusTab.airflow_dags || [];
    const envName =
      selectedAirflowEnvironment?.name || selectedAirflowEnvironment;
    if (!envName || airflowPipelines?.length) {
      return dags.length;
    }
    return dags.filter((dag) => {
      const dagEnv =
        dag.environment_name ||
        dag.environmentName ||
        dag.workspace_name ||
        dag.workspaceName ||
        dag.environment;
      return !dagEnv || dagEnv === envName;
    }).length;
  }, [
    pipelinesByStatusTab.airflow_dags,
    selectedAirflowEnvironment,
    airflowPipelines,
  ]);

  // Handle account selection (also persists to URL for navigation-safe restore)
  const handleAccountSelect = (accountId) => {
    const account = accountId
      ? awsAccounts.find((acc) => acc.accountId === accountId)
      : null;
    setSelectedAccount(account);
    try {
      if (accountId) {
        localStorage.setItem(
          "clairai_selected_aws_account_id",
          String(accountId).replace(/^aws_/, ""),
        );
      } else {
        localStorage.removeItem("clairai_selected_aws_account_id");
      }
    } catch {
      /* ignore */
    }
    setDiscoveryResults(null);
    setStatusSummary(null);
    setOnboardedPipelinesFromApi(null);
    setStatusApiError("");
    setSelectedPipelines({
      glue: new Set(),
      stepfunctions: new Set(),
      emr: new Set(),
      airflow: new Set(),
    });
    setSelectedLogPaths({ cloudwatch: new Set(), s3: new Set() });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (accountId) next.set("accountId", accountId);
      else next.delete("accountId");
      return next;
    });
  };

  // Restore selected account from URL when page loads or user navigates back
  useEffect(() => {
    const accountIdFromUrl = searchParams.get("accountId");
    if (accountIdFromUrl && awsAccounts.length > 0) {
      const account = awsAccounts.find(
        (acc) => acc.accountId === accountIdFromUrl,
      );
      if (account) {
        setSelectedAccount(account);
      }
    }
  }, [searchParams, awsAccounts]);

  // Fetch status & onboarded pipelines when account selected (fast DynamoDB APIs)
  useEffect(() => {
    if (!selectedAccount || !user?.client_id) {
      setStatusSummary(null);
      setOnboardedPipelinesFromApi(null);
      setStatusApiError("");
      return;
    }
    const clientId = selectedAccount.clientId || user.client_id;
    const awsAccountId = selectedAccount.accountId;
    setStatusApiLoading(true);
    setStatusApiError("");
    Promise.all([
      pipelineDiscoveryService.getPipelineStatus(clientId, awsAccountId),
      pipelineDiscoveryService.getOnboardedPipelines(clientId, awsAccountId),
    ])
      .then(([statusRes, onboardedRes]) => {
        setStatusSummary(statusRes);
        setOnboardedPipelinesFromApi(onboardedRes?.pipelines || []);
      })
      .catch((err) => {
        console.error("Status/Onboarded API error:", err);
        setStatusSummary(null);
        setOnboardedPipelinesFromApi(null);
        setStatusApiError(
          err?.message ||
            "Failed to load pipeline status summary. You can still run discovery manually.",
        );
        message.error(
          "Could not load saved pipeline status. Try Refresh or Discover Pipelines.",
        );
      })
      .finally(() => {
        setStatusApiLoading(false);
      });
  }, [selectedAccount?.accountId, selectedAccount?.clientId, user?.client_id]);

  // Run live discovery automatically when account changes so totals are current
  // without requiring a manual click on "Discover Pipelines".
  useEffect(() => {
    if (!selectedAccount || !user?.client_id) return;
    const clientId = selectedAccount.clientId || user.client_id;
    const awsAccountId = selectedAccount.accountId;
    setDiscoveryLoading(true);

    pipelineDiscoveryService
      .discoverPipelines(clientId, awsAccountId, {
        regions: null,
        autoOnboard: false,
      })
      .then((result) => {
        setDiscoveryResults(result);
        setAirflowPipelines([]);
      })
      .catch((err) => {
        console.error("Auto pipeline discovery error:", err);
      })
      .finally(() => {
        setDiscoveryLoading(false);
      });
  }, [selectedAccount?.accountId, selectedAccount?.clientId, user?.client_id]);

  // Handle pipeline discovery
  const handleDiscoverPipelines = async () => {
    if (!selectedAccount) {
      message.warning("Please select an AWS account first");
      return;
    }
    const clientId = selectedAccount.clientId || user?.client_id;
    if (!clientId) {
      message.error("Missing client id; please re-login and try again.");
      return;
    }

    setDiscoveryLoading(true);
    setDiscoveryResults(null);
    setSelectedPipelines({
      glue: new Set(),
      stepfunctions: new Set(),
      emr: new Set(),
      airflow: new Set(),
    });
    setSelectedLogPaths({ cloudwatch: new Set(), s3: new Set() });

    try {
      const result = await pipelineDiscoveryService.discoverPipelines(
        clientId,
        selectedAccount.accountId,
        {
          regions: null,
          autoOnboard: false, // We'll let user select which to onboard
        },
      );

      setDiscoveryResults(result);
      setAirflowPipelines([]);
      message.success(
        `Discovered ${result.summary.total_pipelines} pipelines with ${result.summary.total_log_paths} log paths`,
      );
    } catch (error) {
      console.error("Pipeline discovery error:", error);
      message.error(error.message || "Failed to discover pipelines");
    } finally {
      setDiscoveryLoading(false);
    }
  };

  // Databricks: Discover pipelines
  const handleDiscoverDatabricksPipelines = async () => {
    const wsName = selectedDatabricksWorkspace?.workspace_name || selectedDatabricksWorkspace?.deployment_name;
    if (!wsName) {
      message.warning("Please select a Databricks workspace");
      return;
    }
    setDatabricksPipelinesLoading(true);
    setDatabricksPipelines([]);
    try {
      const res = await databricksPipelinesService.discoverPipelines(wsName);
      setDatabricksPipelines(res.items || []);
      message.success(`Discovered ${res.count ?? res.items?.length ?? 0} pipelines`);
    } catch (err) {
      console.error("Databricks discover error:", err);
      message.error(err.message || "Failed to discover pipelines");
    } finally {
      setDatabricksPipelinesLoading(false);
    }
  };

  // Databricks: Toggle pipeline (enable/disable)
  const handleDatabricksPipelineToggle = async (pipeline, enable) => {
    const pipelineId = pipeline.id;
    const wsName = selectedDatabricksWorkspace?.workspace_name || selectedDatabricksWorkspace?.deployment_name;
    const awsAccountId = databricksAwsAccountId || awsAccounts[0]?.accountId;
    if (!wsName || !awsAccountId || String(awsAccountId).length !== 12) {
      message.warning("Workspace and AWS account (12-digit) are required");
      return;
    }
    setDatabricksPipelineToggling(pipelineId);
    try {
      if (enable) {
        await databricksPipelinesService.enablePipeline(wsName, awsAccountId, {
          pipeline_id: pipelineId,
          enable_logs: true,
          enable_metrics: true,
        });
        message.success(`Enabled monitoring for ${pipeline.name || pipelineId}`);
      } else {
        await databricksPipelinesService.disablePipeline(pipelineId, wsName, awsAccountId);
        message.success(`Disabled monitoring for ${pipeline.name || pipelineId}`);
      }
      await handleDiscoverDatabricksPipelines();
    } catch (err) {
      message.error(err.message || "Failed to update pipeline");
    } finally {
      setDatabricksPipelineToggling(null);
    }
  };

  // Airflow (MWAA): Discover DAGs from selected environment
  const handleDiscoverAirflowPipelines = async (override = {}) => {
    const envName =
      override.environmentName ||
      selectedAirflowEnvironment?.name ||
      selectedAirflowEnvironment;
    const envRegion =
      override.environmentRegion ||
      selectedAirflowEnvironment?.region ||
      null;
    const awsAccountId = selectedAccount?.accountId;
    const clientId = selectedAccount?.clientId || user?.client_id;
    if (!envName) {
      message.warning("Please select an Airflow environment");
      return;
    }
    if (!clientId) {
      message.warning("Client ID is required to refresh DAGs");
      return;
    }
    setAirflowPipelinesLoading(true);
    try {
      const res = await airflowPipelinesService.discoverDags(
        envName,
        awsAccountId,
        envRegion,
        clientId,
      );
      const discoverySource = res.debug?.source || res.source || null;
      const normalizedItems = (res.items || []).map((item) =>
        normalizeAirflowPipelineItem(item, envName, envRegion, discoverySource),
      );
      setAirflowPipelines(normalizedItems);
      setDiscoveryResults((prev) => {
        if (!prev?.pipelines) return prev;
        const otherDags = (prev.pipelines.airflow_dags || []).filter((dag) => {
          const dagEnv = getAirflowEnvironmentName(dag);
          return dagEnv && dagEnv !== envName;
        });
        return {
          ...prev,
          pipelines: {
            ...prev.pipelines,
            airflow_dags: dedupePipelinesByStableKey([
              ...otherDags,
              ...normalizedItems,
            ]),
          },
          summary: {
            ...prev.summary,
            total_airflow_dags: dedupePipelinesByStableKey([
              ...otherDags,
              ...normalizedItems,
            ]).length,
            total_pipelines:
              (prev.pipelines.glue_jobs?.length || 0) +
              (prev.pipelines.step_functions?.length || 0) +
              (prev.pipelines.emr_clusters?.length || 0) +
              dedupePipelinesByStableKey([...otherDags, ...normalizedItems])
                .length,
          },
        };
      });
      message.success(`Discovered ${res.count ?? normalizedItems.length} DAG(s)`);
    } catch (err) {
      console.error("Airflow discover error:", err);
      message.error(err.message || "Failed to discover DAGs");
      setAirflowPipelines([]);
    } finally {
      setAirflowPipelinesLoading(false);
    }
  };

  // Airflow (MWAA): Toggle DAG (enable/disable monitoring)
  const handleAirflowPipelineToggle = async (dag, enable) => {
    const envName =
      dag.environment_name ||
      dag.environmentName ||
      dag.workspace_name ||
      dag.workspaceName ||
      dag.environment ||
      selectedAirflowEnvironment?.name ||
      selectedAirflowEnvironment;
    const envRegion =
      dag.region || selectedAirflowEnvironment?.region || null;
    const awsAccountId = selectedAccount?.accountId;
    const clientId = selectedAccount?.clientId || user?.client_id;
    if (!envName || !awsAccountId) {
      message.warning("Environment and AWS account are required");
      return;
    }
    if (!clientId) {
      message.warning("Client ID is required");
      return;
    }
    const dagId = dag.dag_id || dag.id || dag.name;
    if (!dagId) return;
    setAirflowPipelineToggling(dagId);
    try {
      if (!selectedAirflowEnvironment) {
        setSelectedAirflowEnvironment({ name: envName, region: envRegion });
      }
      if (enable) {
        await airflowPipelinesService.enableDag(
          envName,
          awsAccountId,
          {
            dag_id: dagId,
            enable_logs: true,
            enable_metrics: true,
          },
          clientId,
        );
        message.success(`Enabled monitoring for ${dag.name || dagId}`);
      } else {
        await airflowPipelinesService.disableDag(
          dagId,
          envName,
          awsAccountId,
          clientId,
        );
        message.success(`Disabled monitoring for ${dag.name || dagId}`);
      }
      await handleDiscoverAirflowPipelines({
        environmentName: envName,
        environmentRegion: envRegion,
      });
    } catch (err) {
      message.error(err.message || "Failed to update DAG");
    } finally {
      setAirflowPipelineToggling(null);
    }
  };

  // Update log paths based on selected pipelines
  const updateLogPathsForSelectedPipelines = useCallback(
    (selected) => {
      if (!discoveryResults || !discoveryPipelineGroups) return;

      const { cloudwatch, s3 } = collectLogPathsForSelectedPipelines(
        selected,
        discoveryPipelineGroups,
        discoveryResults,
      );

      setSelectedLogPaths({ cloudwatch, s3 });
    },
    [discoveryResults, discoveryPipelineGroups],
  );

  // Handle pipeline selection
  const handlePipelineToggle = (pipelineType, pipelineIndex, checked) => {
    const currentSet = selectedPipelines[pipelineType] || new Set();
    const nextSet = new Set(currentSet);
    if (checked) {
      nextSet.add(pipelineIndex);
    } else {
      nextSet.delete(pipelineIndex);
    }
    const nextSelected = { ...selectedPipelines, [pipelineType]: nextSet };
    setSelectedPipelines(nextSelected);
    updateLogPathsForSelectedPipelines(nextSelected);
  };

  // Onboard all new log paths (select all new and run onboarding)
  const handleOnboardAllNew = async () => {
    if (!selectedAccount) {
      message.warning("Please select an AWS account");
      return;
    }
    const clientId = user?.client_id;
    if (!clientId) {
      message.error("Missing client id; please re-login and try again.");
      return;
    }
    if (!discoveryResults) return;
    const cloudwatchLogInfos = (discoveryResults.log_paths?.cloudwatch || [])
      .filter((item) => !(typeof item === "object" ? item.onboarded : false))
      .map((item) =>
        typeof item === "object"
          ? item
          : {
              path: item,
              onboarded: false,
              pipeline_name: null,
              pipeline_type: null,
              pipeline_region: null,
            },
      );
    const s3LogInfos = (discoveryResults.log_paths?.s3 || [])
      .filter((item) => !(typeof item === "object" ? item.onboarded : false))
      .map((item) =>
        typeof item === "object"
          ? item
          : {
              path: item,
              onboarded: false,
              pipeline_name: null,
              pipeline_type: null,
              pipeline_region: null,
            },
      );
    const total = cloudwatchLogInfos.length + s3LogInfos.length;
    if (total === 0) {
      message.info("No new log paths to onboard");
      return;
    }
    setOnboardingLoading(true);
    try {
      const result = await pipelineDiscoveryService.autoOnboardLogs(
        clientId,
        selectedAccount.accountId,
        { cloudwatch: cloudwatchLogInfos, s3: s3LogInfos },
        {
          onboardCloudwatch: cloudwatchLogInfos.length > 0,
          onboardS3: s3LogInfos.length > 0,
        },
      );
      setOnboardingResults(result);
      const onboarded = result.summary?.total_onboarded || 0;
      message.success(`Successfully onboarded ${onboarded} log path(s)!`);
      await handleDiscoverPipelines();
      setSelectedLogPaths({ cloudwatch: new Set(), s3: new Set() });
    } catch (error) {
      console.error("Onboard All New error:", error);
      if (error?.status === 409) {
        message.warning(error.message || "Selected log path is already onboarded.");
      } else {
        message.error(error.message || "Failed to onboard");
      }
    } finally {
      setOnboardingLoading(false);
    }
  };

  // Handle log path selection
  const handleLogPathToggle = (logType, logPath, checked) => {
    setSelectedLogPaths((prev) => {
      const currentSet = prev[logType] || new Set();
      const nextSet = new Set(currentSet);
      if (checked) {
        nextSet.add(logPath);
      } else {
        nextSet.delete(logPath);
      }
      return { ...prev, [logType]: nextSet };
    });
  };

  // Handle onboarding selected pipelines/logs
  const handleOnboardSelected = async () => {
    if (!selectedAccount) {
      message.warning("Please select an AWS account");
      return;
    }

    if (!discoveryResults) {
      message.warning("No discovery results available");
      return;
    }

    // Convert selected log path strings to LogPathInfo objects with pipeline information
    const cloudwatchLogInfos = Array.from(selectedLogPaths.cloudwatch).map(
      (path) => {
        // Find the log path info from discovery results
        const logPathInfo = discoveryResults.log_paths?.cloudwatch?.find(
          (item) => {
            const itemPath = typeof item === "object" ? item.path : item;
            return itemPath === path;
          },
        );

        if (logPathInfo && typeof logPathInfo === "object") {
          // Already a LogPathInfo object with pipeline info
          return logPathInfo;
        } else {
          // Convert string to LogPathInfo object
          // Try to find pipeline info from the grouped data (logPathsData is available in scope)
          let foundPipelineInfo = null;
          for (const group of logPathsData) {
            const matchingLogPath = group.logPaths.find(
              (lp) => lp.path === path && lp.type === "cloudwatch",
            );
            if (matchingLogPath) {
              foundPipelineInfo = {
                path: path,
                onboarded: matchingLogPath.onboarded || false,
                pipeline_name: group.pipelineName || null,
                pipeline_type: group.pipelineType || null,
                pipeline_region: group.pipelineRegion || null,
              };
              break;
            }
          }

          // Return found info or create minimal LogPathInfo
          return (
            foundPipelineInfo || {
              path: path,
              onboarded: false,
              pipeline_name: null,
              pipeline_type: null,
              pipeline_region: null,
            }
          );
        }
      },
    );

    const s3LogInfos = Array.from(selectedLogPaths.s3).map((path) => {
      // Find the log path info from discovery results
      const logPathInfo = discoveryResults.log_paths?.s3?.find((item) => {
        const itemPath = typeof item === "object" ? item.path : item;
        return itemPath === path;
      });

      if (logPathInfo && typeof logPathInfo === "object") {
        // Already a LogPathInfo object with pipeline info
        return logPathInfo;
      } else {
        // Convert string to LogPathInfo object
        // Try to find pipeline info from the grouped data
        let foundPipelineInfo = null;
        for (const group of logPathsData) {
          const matchingLogPath = group.logPaths.find(
            (lp) => lp.path === path && lp.type === "s3",
          );
          if (matchingLogPath) {
            foundPipelineInfo = {
              path: path,
              onboarded: matchingLogPath.onboarded || false,
              pipeline_name: group.pipelineName || null,
              pipeline_type: group.pipelineType || null,
              pipeline_region: group.pipelineRegion || null,
            };
            break;
          }
        }

        // Return found info or create minimal LogPathInfo
        return (
          foundPipelineInfo || {
            path: path,
            onboarded: false,
            pipeline_name: null,
            pipeline_type: null,
            pipeline_region: null,
          }
        );
      }
    });

    // Convert selected metrics to MetricInfo objects
    const selectedMetricsList = Array.from(selectedMetrics)
      .map((metricKey) => {
        // metricKey format: "namespace:name:pipeline_name:pipeline_type"
        const parts = metricKey.split(":");
        if (parts.length >= 4) {
          const [namespace, name, pipelineName, pipelineType] = parts;
          // Find the full metric info from discovery results
          const fullMetric = discoveryResults.metrics?.find(
            (m) =>
              m.namespace === namespace &&
              m.name === name &&
              m.pipeline_name === pipelineName &&
              m.pipeline_type === pipelineType,
          );
          return (
            fullMetric || {
              name,
              namespace,
              statistics: ["Sum", "Average"],
              dimensions: {},
              onboarded: false,
              pipeline_name: pipelineName,
              pipeline_type: pipelineType,
              pipeline_region: null,
            }
          );
        }
        return null;
      })
      .filter(Boolean);

    if (
      cloudwatchLogInfos.length === 0 &&
      s3LogInfos.length === 0 &&
      selectedMetricsList.length === 0
    ) {
      message.warning(
        "Please select at least one log path or metric to onboard",
      );
      return;
    }

    const pendingCloudwatch = cloudwatchLogInfos.filter((item) => !item.onboarded);
    const pendingS3 = s3LogInfos.filter((item) => !item.onboarded);

    if (
      pendingCloudwatch.length === 0 &&
      pendingS3.length === 0 &&
      selectedMetricsList.length === 0
    ) {
      const uniqueSelected = cloudwatchLogInfos.length + s3LogInfos.length;
      message.info(
        `All ${uniqueSelected} unique log path${uniqueSelected !== 1 ? "s" : ""} from the selected pipelines are already onboarded.`,
      );
      return;
    }

    const clientId = selectedAccount.clientId || user?.client_id;
    if (!clientId) {
      message.error("Missing client id; please re-login and try again.");
      return;
    }

    setOnboardingLoading(true);

    try {
      const result = await pipelineDiscoveryService.autoOnboardLogs(
        clientId,
        selectedAccount.accountId,
        {
          cloudwatch: cloudwatchLogInfos,
          s3: s3LogInfos,
        },
        {
          onboardCloudwatch: cloudwatchLogInfos.length > 0,
          onboardS3: s3LogInfos.length > 0,
          onboardMetrics: selectedMetricsList.length > 0,
          metrics: selectedMetricsList,
        },
      );

      // Store results for display
      setOnboardingResults(result);

      // Show success message with details
      const totalOnboarded = result.summary.total_onboarded || 0;
      const totalAlreadyOnboarded = result.summary.total_already_onboarded || 0;
      const totalFailed = result.summary.total_failed || 0;
      const metricsOnboarded = result.summary.metrics_onboarded || 0;
      const metricsFailed = result.summary.metrics_failed || 0;

      const totalOnboardedAll = totalOnboarded + metricsOnboarded;

      if (totalOnboardedAll > 0) {
        const parts = [];
        if (totalOnboarded > 0) parts.push(`${totalOnboarded} log path(s)`);
        if (metricsOnboarded > 0)
          parts.push(`${metricsOnboarded} metric service(s)`);
        message.success(
          `Successfully onboarded ${parts.join(" and ")}!` +
            (totalAlreadyOnboarded > 0
              ? ` ${totalAlreadyOnboarded} were already onboarded.`
              : ""),
        );
      } else if (totalAlreadyOnboarded > 0) {
        message.info(
          `${totalAlreadyOnboarded} item(s) were already onboarded.`,
        );
      }

      // Show detailed results modal only for new onboardings or failures
      if (totalFailed > 0 || totalOnboarded > 0 || metricsOnboarded > 0) {
        setShowOnboardingResultsModal(true);
      }

      // Refresh discovery to show updated status
      await handleDiscoverPipelines();
    } catch (error) {
      console.error("Onboarding error:", error);
      if (error?.status === 409) {
        message.warning(error.message || "Selected log path is already onboarded.");
      } else {
        message.error(error.message || "Failed to onboard logs");
      }
    } finally {
      setOnboardingLoading(false);
    }
  };

  const loadLineageView = useCallback(
    async (pipeline) => {
      const clientId = selectedAccount?.clientId || user?.client_id;
      const targetPipelineId = pipeline?.id || pipeline?.pipeline_id;
      const targetPipelineName = normalizeText(
        pipeline?.name || pipeline?.pipeline_name,
      );
      const targetPipelineType = normalizePipelineType(
        pipeline?.type || pipeline?.pipeline_type,
      );
      const targetPipelineRegion = normalizeText(
        pipeline?.region || pipeline?.pipeline_region,
      );

      if (!clientId || (!targetPipelineId && !targetPipelineName)) {
        setLineageView(EMPTY_LINEAGE_VIEW);
        setLineageViewError("");
        setLineageViewLoading(false);
        return;
      }

      setLineageViewLoading(true);
      setLineageViewError("");
      setLineageView(EMPTY_LINEAGE_VIEW);

      try {
        const diagrams = await pipelineDiscoveryService.listWorkflowDiagrams(
          clientId,
        );
        const workflowNames = (diagrams || [])
          .map((d) => (typeof d === "string" ? d : d?.name))
          .filter(Boolean);

        if (workflowNames.length === 0) {
          setLineageView({
            ...EMPTY_LINEAGE_VIEW,
            scannedWorkflows: 0,
            matchedWorkflows: 0,
          });
          return;
        }

        const diagramPayloads = await Promise.all(
          workflowNames.map(async (name) => {
            try {
              const diagram = await pipelineDiscoveryService.loadWorkflowDiagram(
                clientId,
                name,
              );
              return { name, diagram };
            } catch (error) {
              console.warn(
                `[PipelineDiscoveryPage] Failed to load workflow "${name}" for lineage preview`,
                error,
              );
              return { name, diagram: null };
            }
          }),
        );

        const upstreamMap = new Map();
        const downstreamMap = new Map();
        const lineagePaths = [];
        const matchedWorkflowNames = new Set();

        const addRelatedNode = (targetMap, nodeById, nodeId, workflowName, matchedNodeIds) => {
          if (matchedNodeIds.has(nodeId)) return;
          const node = nodeById.get(nodeId);
          if (!node) return;

          const summary = getNodePipelineSummary(node);
          const dedupeKey =
            summary.pipelineId ||
            `${normalizeText(summary.pipelineName)}|${normalizePipelineType(summary.pipelineType)}|${normalizeText(summary.region)}`;
          if (!dedupeKey) return;

          if (!targetMap.has(dedupeKey)) {
            targetMap.set(dedupeKey, {
              ...summary,
              workflows: new Set(),
            });
          }
          targetMap.get(dedupeKey).workflows.add(workflowName);
        };

        for (const item of diagramPayloads) {
          const name = item?.name;
          const diagram = item?.diagram;
          if (!name || !diagram) continue;

          const nodes = Array.isArray(diagram.nodes) ? diagram.nodes : [];
          const edges = Array.isArray(diagram.edges) ? diagram.edges : [];
          if (nodes.length === 0) continue;

          const nodeById = new Map(
            nodes.filter((n) => n?.id).map((n) => [n.id, n]),
          );

          const matchedNodeIds = new Set(
            nodes
              .filter((node) => {
                const summary = getNodePipelineSummary(node);
                if (
                  targetPipelineId &&
                  summary.pipelineId &&
                  summary.pipelineId === targetPipelineId
                ) {
                  return true;
                }

                const nodeName = normalizeText(summary.pipelineName);
                if (!targetPipelineName || !nodeName) return false;
                if (nodeName !== targetPipelineName) return false;

                const nodeType = normalizePipelineType(summary.pipelineType);
                const nodeRegion = normalizeText(summary.region);
                const typeMatches =
                  !targetPipelineType ||
                  !nodeType ||
                  nodeType === targetPipelineType;
                const regionMatches =
                  !targetPipelineRegion ||
                  !nodeRegion ||
                  nodeRegion === targetPipelineRegion;
                return typeMatches && regionMatches;
              })
              .map((node) => node.id),
          );

          if (matchedNodeIds.size === 0) continue;

          matchedWorkflowNames.add(name);

          matchedNodeIds.forEach((nodeId) => {
            const steps = buildLineagePathForNode(nodeId, nodeById, edges);
            if (steps.length > 0) {
              lineagePaths.push({ workflowName: name, steps });
            }
          });

          const upstreamIds = collectReachableNodeIds(
            Array.from(matchedNodeIds),
            edges,
            "upstream",
          );
          const downstreamIds = collectReachableNodeIds(
            Array.from(matchedNodeIds),
            edges,
            "downstream",
          );

          upstreamIds.forEach((nodeId) =>
            addRelatedNode(
              upstreamMap,
              nodeById,
              nodeId,
              name,
              matchedNodeIds,
            ),
          );
          downstreamIds.forEach((nodeId) =>
            addRelatedNode(
              downstreamMap,
              nodeById,
              nodeId,
              name,
              matchedNodeIds,
            ),
          );
        }

        const normalizeRelatedList = (relatedMap) =>
          Array.from(relatedMap.values())
            .map((entry) => ({
              ...entry,
              workflows: Array.from(entry.workflows).sort(),
            }))
            .sort((a, b) =>
              String(a.pipelineName || "").localeCompare(
                String(b.pipelineName || ""),
              ),
            );

        setLineageView({
          upstream: normalizeRelatedList(upstreamMap),
          downstream: normalizeRelatedList(downstreamMap),
          paths: lineagePaths,
          workflowNames: Array.from(matchedWorkflowNames).sort(),
          scannedWorkflows: workflowNames.length,
          matchedWorkflows: matchedWorkflowNames.size,
        });
      } catch (error) {
        console.error("[PipelineDiscoveryPage] Failed to load lineage view", error);
        setLineageView(EMPTY_LINEAGE_VIEW);
        setLineageViewError(
          error?.message || "Failed to load saved lineage details",
        );
      } finally {
        setLineageViewLoading(false);
      }
    },
    [selectedAccount?.clientId, user?.client_id],
  );

  const refreshPipelineModalMetrics = useCallback(
    async (pipeline) => {
      const pipelineId = pipeline?.pipeline_id;
      const clientId = selectedAccount?.clientId || user?.client_id;
      const awsAccountId = selectedAccount?.accountId;
      if (!pipelineId || !clientId || !awsAccountId) return [];

      const discoveryMetrics = resolvePipelineMetrics(
        pipeline,
        discoveryResults,
      );
      const displayMetrics =
        (pipeline?.metrics || []).length > 0
          ? pipeline.metrics
          : (selectedPipelineForModal?.metrics || []).length > 0
            ? selectedPipelineForModal.metrics
            : discoveryMetrics;

      let metrics = displayMetrics;
      try {
        const metricsResp = await pipelineDiscoveryService.getMetricsByPipeline(
          pipelineId,
          { clientId, awsAccountId },
        );
        if (metricsResp?.metrics?.length) {
          metrics = mergeMetricsOnboardedFlags(
            normalizeMetricsFromApi(metricsResp.metrics),
            sanitizeDiscoveryMetricFlags(displayMetrics),
          );
        }
      } catch (metricsErr) {
        console.warn("[refreshPipelineModalMetrics]", metricsErr);
      }
      setSelectedPipelineForModal((prev) =>
        prev ? { ...prev, metrics } : prev,
      );
      return metrics;
    },
    [
      discoveryResults,
      selectedAccount?.accountId,
      selectedAccount?.clientId,
      selectedPipelineForModal?.metrics,
      user?.client_id,
    ],
  );

  const handleOnboardPipelineMetrics = async (pipeline, metricsToOnboard) => {
    if (!selectedAccount) {
      message.warning("Please select an AWS account");
      return;
    }
    const clientId = selectedAccount.clientId || user?.client_id;
    if (!clientId) {
      message.error("Missing client id; please re-login and try again.");
      return;
    }
    const list = (metricsToOnboard || [])
      .filter((m) => m && !m.onboarded)
      .map((m) => ({
        ...m,
        pipeline_name: m.pipeline_name || pipeline.name,
        pipeline_type: m.pipeline_type || pipeline.type || pipeline.pipeline_type,
        pipeline_region:
          m.pipeline_region || pipeline.region || pipeline.pipeline_region,
      }));
    if (!list.length) {
      message.info("No metrics selected to onboard");
      return;
    }

    setOnboardingModalMetrics(true);
    try {
      const result = await pipelineDiscoveryService.autoOnboardLogs(
        clientId,
        selectedAccount.accountId,
        { cloudwatch: [], s3: [] },
        {
          onboardCloudwatch: false,
          onboardS3: false,
          onboardMetrics: true,
          metrics: list,
        },
      );
      const onboarded = result?.summary?.metrics_onboarded || 0;
      const already = result?.summary?.metrics_already_onboarded || 0;
      const failed = result?.summary?.metrics_failed || 0;

      const apiOnboardedKeys = new Set(
        [
          ...(result?.results?.metrics?.onboarded || []),
          ...(result?.results?.metrics?.already_onboarded || []),
        ]
          .filter((m) => m && m.name && m.namespace)
          .map((m) => metricSelectionKey(m)),
      );

      const metricsWithOnboarded = (
        selectedPipelineForModal?.metrics ||
        pipeline.metrics ||
        []
      ).map((m) =>
        apiOnboardedKeys.has(metricSelectionKey(m))
          ? { ...m, onboarded: true }
          : m,
      );

      if (onboarded > 0 || already > 0) {
        setSelectedPipelineForModal((prev) =>
          prev ? { ...prev, metrics: metricsWithOnboarded } : prev,
        );
      }

      if (onboarded > 0) {
        message.success(
          `Onboarded ${onboarded} metric${onboarded !== 1 ? "s" : ""} for monitoring`,
        );
      } else if (already > 0 && failed === 0) {
        message.info("Selected metrics are already onboarded");
      } else if (failed > 0) {
        message.warning(`Some metrics failed to onboard (${failed})`);
      } else {
        message.warning(
          "No metrics were persisted. Check backend logs and try again.",
        );
      }

      setModalSelectedMetricKeys(new Set());
      const updatedPipeline = {
        ...pipeline,
        metrics: metricsWithOnboarded,
      };
      await refreshPipelineModalMetrics(updatedPipeline);
      await refreshSinglePipelineInDiscovery(updatedPipeline);
    } catch (error) {
      console.error("[handleOnboardPipelineMetrics]", error);
      message.error(error.message || "Failed to onboard metrics");
    } finally {
      setOnboardingModalMetrics(false);
    }
  };

  const closePipelineDetailModal = () => {
    setPipelineDetailModalOpen(false);
    setSelectedPipelineForModal(null);
    setModalSelectedMetricKeys(new Set());
    setActivePipelineDetailTab("logs");
    setPipelineAlertsOnly(false);
    setPipelineDetailLoading(false);
    setLineageViewLoading(false);
    setLineageViewError("");
    setLineageView(EMPTY_LINEAGE_VIEW);
    setGrafanaLinkUrl(null);
    setGrafanaLinkLoading(false);
  };

  // Open pipeline detail modal; load logs/metrics from API with discovery fallbacks
  const handleOpenPipelineModal = async (pipeline, options = {}) => {
    const { alertsOnly = false } = options;
    const initialLogPaths = resolvePipelineLogPaths(pipeline, discoveryResults);
    setModalSelectedMetricKeys(new Set());
    setModalMwaaNamespaceFilter("all");
    setPipelineAlertsOnly(alertsOnly);
    setSelectedPipelineForModal({
      ...pipeline,
      metrics: [],
      log_paths: initialLogPaths,
    });
    setActivePipelineDetailTab(alertsOnly ? "alerts" : "logs");
    setLineageEnabled(pipeline?.lineage_enabled !== false);
    setLineageView(EMPTY_LINEAGE_VIEW);
    setLineageViewError("");
    setPipelineDetailModalOpen(true);
    if (alertsOnly) {
      setPipelineDetailLoading(false);
      return;
    }
    loadLineageView(pipeline);
    const pipelineId = pipeline.pipeline_id;
    if (!pipelineId) return;

    setPipelineDetailLoading(true);
    const clientId = selectedAccount?.clientId || user?.client_id;
    const awsAccountId = selectedAccount?.accountId;

    try {
      let logPaths = resolvePipelineLogPaths(pipeline, discoveryResults);
      const discoveryLogPaths = resolvePipelineLogPaths(
        pipeline,
        discoveryResults,
      );
      let metrics = [];
      const discoveryMetrics = resolvePipelineMetrics(pipeline, discoveryResults);
      let lineageEnabled = pipeline?.lineage_enabled !== false;

      if (clientId && awsAccountId) {
        try {
          const logsResp = await pipelineDiscoveryService.getLogsByPipeline(
            pipelineId,
            { clientId, awsAccountId },
          );
          const apiLogs = logsResp?.logs || [];
          if (apiLogs.length) {
            const fromApi = apiLogItemsToPathGroups(apiLogs);
            const discTotal =
              discoveryLogPaths.cloudwatch.length +
              discoveryLogPaths.s3.length;
            const apiTotal =
              fromApi.cloudwatch.length + fromApi.s3.length;
            logPaths =
              discTotal > apiTotal
                ? mergeLogPathsWithApi(discoveryLogPaths, apiLogs)
                : fromApi;
          }
        } catch (logsErr) {
          console.warn("[handleOpenPipelineModal] logs fetch", logsErr);
        }

        if (!logPaths.cloudwatch.length && !logPaths.s3.length) {
          logPaths = discoveryLogPaths;
        }

        try {
          const metricsResp = await pipelineDiscoveryService.getMetricsByPipeline(
            pipelineId,
            { clientId, awsAccountId },
          );
          if (metricsResp?.metrics?.length) {
            metrics = mergeMetricsOnboardedFlags(
              normalizeMetricsFromApi(metricsResp.metrics),
              sanitizeDiscoveryMetricFlags(discoveryMetrics),
            );
          }
        } catch (metricsErr) {
          console.warn("[handleOpenPipelineModal] metrics fetch", metricsErr);
        }

        if (!metrics.length) {
          metrics = sanitizeDiscoveryMetricFlags(discoveryMetrics);
        }

        try {
          const lineage = await pipelineDiscoveryService.getPipelineLineage(
            pipelineId,
            clientId,
            awsAccountId,
          );
          lineageEnabled = lineage?.enabled !== false;
        } catch (lineageErr) {
          console.warn("[handleOpenPipelineModal] lineage fetch", lineageErr);
        }
      }

      setSelectedPipelineForModal((prev) =>
        prev
          ? {
              ...prev,
              log_paths: logPaths,
              metrics,
              logs_onboarded:
                logPaths.cloudwatch.filter((p) => p.onboarded).length +
                logPaths.s3.filter((p) => p.onboarded).length,
              lineage_enabled: lineageEnabled,
            }
          : prev,
      );
      if (metrics.length && discoveryResults && pipelineId) {
        setDiscoveryResults((prev) =>
          applyPipelineDiscoveryPatch(prev, pipelineId, { metrics }),
        );
      }
      setLineageEnabled(lineageEnabled);
    } catch (err) {
      console.error("[handleOpenPipelineModal]", err);
      message.error("Failed to load pipeline details");
    } finally {
      setPipelineDetailLoading(false);
    }
  };

  const loadGrafanaLink = useCallback(async () => {
    const pipeline = selectedPipelineForModal;
    if (!pipeline || !pipelineDetailModalOpen) {
      setGrafanaLinkUrl(null);
      return;
    }

    setGrafanaLinkLoading(true);
    try {
      const cwPaths = (pipeline.log_paths?.cloudwatch || [])
        .map((p) => (typeof p === "object" ? p.path : p))
        .filter(Boolean);
      const url = await pipelineDiscoveryService.getGrafanaLink({
        pipelineName: pipeline.name,
        pipelineType: pipeline.type || pipeline.pipeline_type,
        tab:
          activePipelineDetailTab === "metrics"
            ? "metrics"
            : activePipelineDetailTab === "lineage"
              ? "lineage"
              : "logs",
        logPaths: cwPaths,
        metrics: pipeline.metrics || [],
        region: pipeline.region || pipeline.pipeline_region,
        pipelineId: pipeline.pipeline_id || (pipeline.type === "emr" ? pipeline.id : null) || pipeline.id,
        pipelineArn: pipeline.arn,
        environmentName: getAirflowEnvironmentName(pipeline),
        awsAccountId: selectedAccount?.accountId,
      });
      setGrafanaLinkUrl(url);
    } catch (error) {
      console.warn("[loadGrafanaLink]", error);
      setGrafanaLinkUrl(null);
    } finally {
      setGrafanaLinkLoading(false);
    }
  }, [
    selectedPipelineForModal,
    pipelineDetailModalOpen,
    activePipelineDetailTab,
  ]);

  useEffect(() => {
    loadGrafanaLink();
  }, [loadGrafanaLink]);

  const handleViewInGrafana = () => {
    if (!grafanaLinkUrl) {
      message.warning("Grafana link is not available right now");
      return;
    }
    window.open(grafanaLinkUrl, "_blank", "noopener,noreferrer");
  };

  const handleSavePipelineLineage = async () => {
    const pipeline = selectedPipelineForModal;
    if (!pipeline) return;
    const pipelineId = pipeline.pipeline_id || pipeline.id;
    const clientId = selectedAccount?.clientId || user?.client_id;
    const awsAccountId = selectedAccount?.accountId;

    if (!pipelineId || !clientId || !awsAccountId) {
      message.warning("Missing account or pipeline details");
      return;
    }

    setLineageSaving(true);
    try {
      const resp = await pipelineDiscoveryService.upsertPipelineLineage({
        clientId,
        awsAccountId,
        pipelineId,
        enabled: lineageEnabled,
        pipelineName: pipeline.name,
        pipelineType: pipeline.type || pipeline.pipeline_type,
        pipelineRegion: pipeline.region || pipeline.pipeline_region,
      });
      setLineageEnabled(Boolean(resp?.enabled));
      setSelectedPipelineForModal((prev) =>
        prev ? { ...prev, lineage_enabled: Boolean(resp?.enabled) } : prev,
      );
      message.success(
        `Pipeline lineage ${resp?.enabled ? "enabled" : "disabled"} successfully`,
      );
    } catch (error) {
      console.error("[handleSavePipelineLineage]", error);
      message.error(error.message || "Failed to update pipeline lineage");
    } finally {
      setLineageSaving(false);
    }
  };

  const handleEditLineageInWorkflow = useCallback(() => {
    const pipeline = selectedPipelineForModal;
    if (!pipeline) return;

    const clientId = selectedAccount?.clientId || user?.client_id;
    const awsAccountId = selectedAccount?.accountId;
    const pipelineId = pipeline.pipeline_id || pipeline.id;

    if (!clientId) {
      message.warning("Missing client details");
      return;
    }

    const q = new URLSearchParams();
    if (awsAccountId) q.set("accountId", awsAccountId);
    q.set("clientId", clientId);
    if (pipelineId) q.set("pipelineId", pipelineId);
    q.set("pipeline", pipeline.name || pipeline.pipeline_name || "");
    q.set("pipelineType", pipeline.type || pipeline.pipeline_type || "");
    q.set("region", pipeline.region || pipeline.pipeline_region || "");
    q.set("editLineage", "1");

    setPipelineDetailModalOpen(false);
    navigate(`/workflows?${q.toString()}`);
  }, [selectedPipelineForModal, selectedAccount?.clientId, selectedAccount?.accountId, user?.client_id, navigate]);

  const getPipelineSyncKey = (pipeline) =>
    pipeline?.pipeline_id || pipeline?.id || pipeline?.name || null;

  const refreshSinglePipelineInDiscovery = useCallback(
    async (pipeline) => {
      const pipelineId = pipeline?.pipeline_id || pipeline?.id;
      const clientId = selectedAccount?.clientId || user?.client_id;
      const awsAccountId = selectedAccount?.accountId;
      if (!pipelineId || !clientId || !awsAccountId) return null;

      const discoveryPipeline =
        getPipelineFromDiscovery(discoveryResults, pipelineId) || pipeline;
      const discoveryLogPaths = resolvePipelineLogPaths(
        discoveryPipeline,
        discoveryResults,
      );
      const discoveryMetrics = resolvePipelineMetrics(
        discoveryPipeline,
        discoveryResults,
      );

      let logPaths = discoveryLogPaths;
      let metrics = discoveryMetrics;

      try {
        const logsResp = await pipelineDiscoveryService.getLogsByPipeline(
          pipelineId,
          { clientId, awsAccountId },
        );
        const apiLogs = logsResp?.logs || [];
        if (apiLogs.length) {
          const fromApi = apiLogItemsToPathGroups(apiLogs);
          const discTotal =
            discoveryLogPaths.cloudwatch.length + discoveryLogPaths.s3.length;
          const apiTotal = fromApi.cloudwatch.length + fromApi.s3.length;
          logPaths =
            discTotal > apiTotal
              ? mergeLogPathsWithApi(discoveryLogPaths, apiLogs)
              : fromApi;
        }
      } catch (logsErr) {
        console.warn("[refreshSinglePipelineInDiscovery] logs", logsErr);
      }

      try {
        const metricsResp = await pipelineDiscoveryService.getMetricsByPipeline(
          pipelineId,
          { clientId, awsAccountId },
        );
        if (metricsResp?.metrics?.length) {
          metrics = mergeMetricsOnboardedFlags(
            normalizeMetricsFromApi(metricsResp.metrics),
            discoveryMetrics,
          );
        }
      } catch (metricsErr) {
        console.warn("[refreshSinglePipelineInDiscovery] metrics", metricsErr);
      }

      setDiscoveryResults((prev) => {
        if (!prev) return prev;
        return applyPipelineDiscoveryPatch(prev, pipelineId, {
          log_paths: logPaths,
          metrics,
        });
      });

      setSelectedPipelineForModal((prev) => {
        if (!prev || !matchPipelineRecord(prev, pipelineId)) return prev;
        return {
          ...prev,
          log_paths: logPaths,
          metrics,
          logs_onboarded:
            logPaths.cloudwatch.filter((p) => p.onboarded).length +
            logPaths.s3.filter((p) => p.onboarded).length,
        };
      });

      return { log_paths: logPaths, metrics };
    },
    [
      discoveryResults,
      selectedAccount?.accountId,
      selectedAccount?.clientId,
      user?.client_id,
    ],
  );

  // Sync pipeline: onboard remaining logs, then remaining metrics, or confirm fully synced
  const handlePipelineSync = (pipeline) => {
    if (!selectedAccount) {
      message.warning("Please select an AWS account");
      return;
    }
    if (pipeline?._fromApi) {
      message.info("Run Discover Pipelines for full sync options");
      return;
    }

    const syncCounts = getPipelineSyncCounts(pipeline, discoveryResults);
    const pipelineName = pipeline.name || pipeline.pipeline_name || "pipeline";

    if (syncCounts.remainingLogs > 0) {
      Modal.confirm({
        title: "Sync log paths?",
        content: `Onboard ${syncCounts.remainingLogs} remaining log path${syncCounts.remainingLogs !== 1 ? "s" : ""} for "${pipelineName}".`,
        okText: "Sync",
        cancelText: "Cancel",
        onOk: () => handleSyncPipeline(pipeline),
      });
      return;
    }

    if (syncCounts.remainingMetrics > 0) {
      const metricsToOnboard = resolvePipelineMetrics(
        pipeline,
        discoveryResults,
      ).filter((m) => !m.onboarded);
      Modal.confirm({
        title: "Sync metrics?",
        content: `Onboard ${syncCounts.remainingMetrics} remaining metric${syncCounts.remainingMetrics !== 1 ? "s" : ""} for "${pipelineName}" into monitoring.`,
        okText: "Sync",
        cancelText: "Cancel",
        onOk: async () => {
          const pipelineKey = getPipelineSyncKey(pipeline);
          setSyncingPipelineId(pipelineKey);
          try {
            await handleOnboardPipelineMetrics(pipeline, metricsToOnboard);
          } finally {
            setSyncingPipelineId(null);
          }
        },
      });
      return;
    }

    const pipelineKey = getPipelineSyncKey(pipeline);
    setSyncingPipelineId(pipelineKey);
    (async () => {
      try {
        await refreshSinglePipelineInDiscovery(pipeline);
        message.success(
          `"${pipelineName}" is fully synced — all logs and metrics are onboarded.`,
        );
      } catch (error) {
        message.error(error.message || "Failed to refresh pipeline status");
      } finally {
        setSyncingPipelineId(null);
      }
    })();
  };

  // Legacy alias used by modal footer
  const confirmAndSyncPipeline = (pipeline) => handlePipelineSync(pipeline);

  // Onboard remaining log paths via auto-onboard API
  const handleSyncPipeline = async (pipeline) => {
    if (!selectedAccount) {
      message.warning("Please select an AWS account");
      return;
    }
    const clientId = selectedAccount.clientId || user?.client_id;
    if (!clientId) {
      message.error("Missing client id; please re-login and try again.");
      return;
    }
    const pipelineKey = getPipelineSyncKey(pipeline);
    setSyncingPipelineId(pipelineKey);
    try {
      const pipelineForSync = {
        ...(getPipelineFromDiscovery(
          discoveryResults,
          pipeline.pipeline_id || pipeline.id,
        ) || pipeline),
        log_paths: resolvePipelineLogPaths(pipeline, discoveryResults),
      };
      const result = await pipelineDiscoveryService.syncPipelineOnboarding(
        clientId,
        selectedAccount.accountId,
        pipelineForSync,
      );
      const totalOnboarded =
        (result?.summary?.total_onboarded ?? 0) +
        (result?.cloudwatch?.onboarded?.length ?? 0) +
        (result?.s3?.onboarded?.length ?? 0);
      if (totalOnboarded === 0) {
        message.info("All log paths are already onboarded for this pipeline");
      } else {
        message.success(
          `Synced ${totalOnboarded} log path${totalOnboarded !== 1 ? "s" : ""} successfully`,
        );
      }
      await refreshSinglePipelineInDiscovery(pipeline);
    } catch (error) {
      console.error("Sync error:", error);
      message.error(error.message || "Failed to sync pipeline");
    } finally {
      setSyncingPipelineId(null);
    }
  };

  // Select all pipelines of a type (uses discovery groups — same indices as pipeline cards)
  const handleSelectAll = (type) => {
    const newSelected = { ...selectedPipelines };
    const pipelineList = getPipelineListForSelectionKey(
      discoveryPipelineGroups,
      type,
    );

    if (newSelected[type].size === pipelineList.length) {
      newSelected[type] = new Set();
    } else {
      newSelected[type] = new Set(pipelineList.map((_, idx) => idx));
    }
    setSelectedPipelines(newSelected);
    updateLogPathsForSelectedPipelines(newSelected);
  };

  // Get summary statistics
  const summaryStats = useMemo(() => {
    if (!discoveryResults) return null;

    const totalSelectedPipelines =
      selectedPipelines.glue.size +
      selectedPipelines.stepfunctions.size +
      selectedPipelines.emr.size +
      selectedPipelines.airflow.size;

    const selectedLogPathSummary = discoveryPipelineGroups
      ? collectLogPathsForSelectedPipelines(
          selectedPipelines,
          discoveryPipelineGroups,
          discoveryResults,
        )
      : null;

    const totalSelectedLogs =
      selectedLogPathSummary?.newUniqueCount ??
      selectedLogPathSummary?.uniqueCount ??
      selectedLogPaths.cloudwatch.size + selectedLogPaths.s3.size;

    const totalSelectedLogsUnique = selectedLogPathSummary?.uniqueCount ?? totalSelectedLogs;

    const selectedLogsAlreadyOnboarded =
      selectedLogPathSummary?.alreadyOnboardedUniqueCount ?? 0;

    const selectedPipelineLogAssociations = discoveryPipelineGroups
      ? countSelectedPipelineLogAssociations(
          selectedPipelines,
          discoveryPipelineGroups,
          discoveryResults,
        )
      : 0;

    const sharedLogPathsDeduped =
      selectedPipelineLogAssociations > totalSelectedLogsUnique
        ? selectedPipelineLogAssociations - totalSelectedLogsUnique
        : 0;

    const totalSelectedMetrics = selectedMetrics.size;

    return {
      totalPipelines: discoveryResults.summary.total_pipelines,
      totalLogPaths: discoveryResults.summary.total_log_paths,
      totalMetrics: discoveryResults.summary.total_metrics || 0,
      totalOnboarded: discoveryResults.summary.total_onboarded || 0,
      totalNew: discoveryResults.summary.total_new || 0,
      metricsOnboarded: discoveryResults.summary.metrics_onboarded || 0,
      metricsNew: discoveryResults.summary.metrics_new || 0,
      selectedPipelines: totalSelectedPipelines,
      selectedLogs: totalSelectedLogs,
      selectedLogsUnique: totalSelectedLogsUnique,
      selectedLogsAlreadyOnboarded,
      selectedPipelineLogAssociations,
      sharedLogPathsDeduped,
      selectedMetrics: totalSelectedMetrics,
    };
  }, [
    discoveryResults,
    discoveryPipelineGroups,
    selectedPipelines,
    selectedLogPaths,
    selectedMetrics,
  ]);

  // Pipeline table columns
  // Card-based pipeline row component (similar to MetricsPage)
  // originalIndices: optional array mapping displayed index -> index in full list (for selection when filtered by tab)
  // emptyMessage: optional custom message when no pipelines (e.g. for filtered tabs)
  const renderPipelineCards = (
    type,
    pipelines,
    originalIndices,
    emptyMessage,
  ) => {
    // Map type to selectedPipelines key
    const selectionKey = type === "step_functions" ? "stepfunctions" : type;
    const data = pipelines || [];
    const indices = originalIndices ?? data.map((_, i) => i);

    const typeDisplayName =
      type === "glue"
        ? "Glue jobs"
        : type === "step_functions"
          ? "Step Functions"
          : type === "emr"
            ? "EMR clusters"
            : type === "airflow"
              ? "Airflow DAGs"
              : type;
    const hasData = data.length > 0;
    const showStatusColumn = type !== "glue";
    const gridClassName = showStatusColumn
      ? "pipeline-discovery-grid"
      : "pipeline-discovery-grid pipeline-discovery-grid--no-status";

    const typeColors = {
      glue: { bg: "#1990FF", border: "#1990FF" },
      step_functions: { bg: "#fa8c16", border: "#fa8c16" },
      emr: { bg: "#722ed1", border: "#722ed1" },
      airflow: { bg: "#13c2c2", border: "#13c2c2" },
    };
    const typeLabels = {
      glue: "Glue",
      step_functions: "Step Functions",
      emr: "EMR",
      airflow: "Airflow",
    };

    return (
      <div
        style={{
          flex: 1,
          minHeight: 300,
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {/* Select All Checkbox - compact */}
        <div
          style={{
            marginBottom: 12,
            padding: "8px 16px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Checkbox
            disabled={!hasData}
            checked={
              hasData &&
              indices.every((origIdx) =>
                selectedPipelines[selectionKey]?.has(origIdx),
              )
            }
            indeterminate={
              indices.filter((origIdx) =>
                selectedPipelines[selectionKey]?.has(origIdx),
              ).length > 0 &&
              indices.filter((origIdx) =>
                selectedPipelines[selectionKey]?.has(origIdx),
              ).length < data.length
            }
            onChange={() => {
              const allSelected = indices.every((origIdx) =>
                selectedPipelines[selectionKey]?.has(origIdx),
              );
              const newSelected = { ...selectedPipelines };
              newSelected[selectionKey] = new Set(
                selectedPipelines[selectionKey] || [],
              );
              if (allSelected) {
                indices.forEach((i) => newSelected[selectionKey].delete(i));
              } else {
                indices.forEach((i) => newSelected[selectionKey].add(i));
              }
              setSelectedPipelines(newSelected);
              updateLogPathsForSelectedPipelines(newSelected);
            }}
          >
            <span
              style={{ color: isDark ? "#adbac7" : "#666", fontWeight: 500 }}
            >
              Select All (
              {
                indices.filter((i) => selectedPipelines[selectionKey]?.has(i))
                  .length
              }
              /{data.length})
            </span>
          </Checkbox>
        </div>

        {/* Pipeline list: header + rows share one scroll container for column alignment */}
        <div
          className="pipeline-discovery-list-scroll"
          style={{
            flex: "1 1 320px",
            minHeight: 320,
            overflowY: "auto",
            overflowX: "auto",
            paddingBottom: 120,
          }}
        >
          <div
            className={`pipeline-discovery-grid-header ${
              showStatusColumn
                ? ""
                : "pipeline-discovery-grid-header--no-status"
            } ${
              isDark
                ? "pipeline-discovery-grid-header--dark"
                : "pipeline-discovery-grid-header--light"
            }`}
            style={{
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            <div className={gridClassName}>
              <div className="pipeline-discovery-grid-col-si">SI</div>
              <div>PIPELINE NAME</div>
              <div className="pipeline-discovery-grid-col-type">TYPE</div>
              <div className="pipeline-discovery-grid-col-region">REGION</div>
              {showStatusColumn ? (
                <div className="pipeline-discovery-grid-col-status">STATUS</div>
              ) : null}
              <div className="pipeline-discovery-grid-col-logs">LOGS</div>
              <div className="pipeline-discovery-grid-col-metrics">METRICS</div>
              <div className="pipeline-discovery-grid-col-onboarding">ONBOARDING</div>
              <div className="pipeline-discovery-grid-col-actions">ACTIONS</div>
              <div className="pipeline-discovery-grid-col-select">SELECT</div>
            </div>
          </div>

          {hasData ? data.map((pipeline, index) => {
          const origIndex = indices[index] ?? index;
          const logCounts = getPipelineLogDisplayCounts(pipeline);
          const totalLogPaths = logCounts.total;
          const totalOnboarded = logCounts.onboarded;

          const metricCounts = getPipelineMetricsDisplayCounts(
            pipeline,
            discoveryResults,
          );
          const metricsOnboarded = metricCounts.onboarded;
          const metricsTotal = metricCounts.total;

          const isSelected =
            selectedPipelines[selectionKey]?.has(origIndex) || false;

          const pipelineOnboardingState = getPipelineOnboardingState(
            pipeline,
            discoveryResults,
          );
          const onboardingStatus =
            pipelineOnboardingState === "full"
              ? "fully"
              : pipelineOnboardingState === "partial"
                ? "partial"
                : "not_onboarded";
          const pipelineId = pipeline.pipeline_id || pipeline.id || pipeline.name;
          const syncCounts = getPipelineSyncCounts(pipeline, discoveryResults);
          const syncTooltip =
            syncCounts.remainingLogs > 0
              ? `Sync — onboard ${syncCounts.remainingLogs} remaining log path${syncCounts.remainingLogs !== 1 ? "s" : ""}`
              : syncCounts.remainingMetrics > 0
                ? `Sync — onboard ${syncCounts.remainingMetrics} remaining metric${syncCounts.remainingMetrics !== 1 ? "s" : ""}`
                : "Sync — all onboarded; click to refresh status";
          const showSyncAction = !pipeline._fromApi;

          return (
            <Card
              key={`${pipeline.pipeline_id || pipeline.id || type}-${index}`}
              className="pipeline-discovery-row-card"
              style={{
                marginBottom: 10,
                borderRadius: 10,
                borderLeft: `4px solid ${typeColors[pipeline.type]?.border || "#1890ff"}`,
                background: isDark ? "#161b22" : "#fff",
                border: isDark ? "1px solid #30363d" : "1px solid #e8e8e8",
                cursor: "pointer",
                overflow: "visible",
              }}
              bodyStyle={{ padding: "10px 14px", minHeight: 58 }}
              onClick={() => handleOpenPipelineModal(pipeline)}
            >
              <div className={gridClassName}>
                {/* SI Number */}
                <div
                  className="pipeline-discovery-grid-col-si"
                  style={{
                    height: 32,
                    borderRadius: 6,
                    background: isDark ? "#21262d" : "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    color: isDark ? "#adbac7" : "#666",
                    fontSize: 12,
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </div>

                {/* Pipeline Name */}
                <div style={{ minWidth: 0, overflow: "hidden" }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: isDark ? "#e6edf3" : "#333",
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={pipeline.name}
                  >
                    {pipeline.name}
                  </div>
                  {isEmrPipeline(pipeline) && pipeline.id && (
                    <div
                      style={{
                        fontSize: 11,
                        color: isDark ? "#58a6ff" : "#1677ff",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={pipeline.id}
                    >
                      {pipeline.id}
                    </div>
                  )}
                  {isAirflowPipeline(pipeline) && getAirflowEnvironmentName(pipeline) && (
                    <div
                      style={{
                        fontSize: 11,
                        color: isDark ? "#8b949e" : "#64748b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={getAirflowEnvironmentName(pipeline)}
                    >
                      {getAirflowEnvironmentName(pipeline)}
                    </div>
                  )}
                  {pipeline.script_location && (
                    <Tooltip title={pipeline.script_location}>
                      <div
                        style={{
                          fontSize: 11,
                          color: isDark ? "#8b949e" : "#999",
                          fontFamily: "monospace",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 200,
                        }}
                      >
                        {pipeline.script_location}
                      </div>
                    </Tooltip>
                  )}
                </div>

                {/* Type */}
                <div className="pipeline-discovery-grid-col-type">
                  <Tag
                    style={{
                      background: typeColors[pipeline.type]?.bg || "#1890ff",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontWeight: 500,
                      padding: "1px 9px",
                      fontSize: 11,
                      lineHeight: "18px",
                    }}
                  >
                    {typeLabels[pipeline.type] || pipeline.type}
                  </Tag>
                </div>

                {/* Region */}
                <div className="pipeline-discovery-grid-col-region">
                  <Tag
                    style={{
                      background: isDark ? "#21262d" : "#f0f0f0",
                      color: isDark ? "#adbac7" : "#666",
                      border: isDark
                        ? "1px solid #30363d"
                        : "1px solid #d9d9d9",
                      borderRadius: 6,
                      padding: "0 7px",
                      fontSize: 11,
                      lineHeight: "18px",
                    }}
                  >
                    {pipeline.region}
                  </Tag>
                </div>

                {showStatusColumn ? (
                  <div className="pipeline-discovery-grid-col-status">
                    {discoveryLoading && !hasPipelineRuntimeStatus(pipeline) ? (
                      <SyncOutlined
                        spin
                        style={{
                          color: isDark ? "#8b949e" : "#999",
                          fontSize: 14,
                        }}
                      />
                    ) : (
                      <PipelineRuntimeStatus
                        pipeline={pipeline}
                        compact
                        isDark={isDark}
                      />
                    )}
                  </div>
                ) : null}

                {/* Logs progress bar */}
                <div
                  className="pipeline-discovery-grid-col-logs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {totalLogPaths > 0 ? (
                    <Tooltip
                      title={`${totalOnboarded} of ${totalLogPaths} log paths onboarded`}
                    >
                      <PipelineOnboardingProgress
                        icon={FileTextOutlined}
                        iconColor="#06b6d4"
                        onboarded={totalOnboarded}
                        total={totalLogPaths}
                        activeColor="#06b6d4"
                        isDark={isDark}
                        kind="logs"
                      />
                    </Tooltip>
                  ) : (
                    <PipelineOnboardingProgress
                      icon={FileTextOutlined}
                      iconColor="#06b6d4"
                      onboarded={0}
                      total={0}
                      activeColor="#06b6d4"
                      isDark={isDark}
                      kind="logs"
                      emptyLabel="Logs 0/0"
                    />
                  )}
                </div>

                {/* Metrics progress bar */}
                <div
                  className="pipeline-discovery-grid-col-metrics"
                  onClick={(e) => e.stopPropagation()}
                >
                  {metricsTotal > 0 ? (
                    <Tooltip
                      title={`${metricsOnboarded} of ${metricsTotal} metrics onboarded`}
                    >
                      <PipelineOnboardingProgress
                        icon={ThunderboltOutlined}
                        iconColor="#7c3aed"
                        onboarded={metricsOnboarded}
                        total={metricsTotal}
                        activeColor="#7c3aed"
                        isDark={isDark}
                        kind="metrics"
                      />
                    </Tooltip>
                  ) : (
                    <PipelineOnboardingProgress
                      icon={ThunderboltOutlined}
                      iconColor="#7c3aed"
                      onboarded={0}
                      total={0}
                      activeColor="#7c3aed"
                      isDark={isDark}
                      kind="metrics"
                      emptyLabel="Metrics 0/0"
                    />
                  )}
                </div>

                {/* Onboarding Status Badge */}
                <div className="pipeline-discovery-grid-col-onboarding">
                  <Tag
                    className={`pipeline-discovery-onboarding-tag pipeline-discovery-onboarding-tag--${
                      onboardingStatus === "fully"
                        ? "full"
                        : onboardingStatus === "partial"
                          ? "partial"
                          : "none"
                    }`}
                  >
                    {onboardingStatus === "fully"
                      ? "Fully Onboarded"
                      : onboardingStatus === "partial"
                        ? "Partially Onboarded"
                        : "Not Onboarded"}
                  </Tag>
                </div>

                {/* Actions: icon buttons with tooltips */}
                <div
                  className="pipeline-discovery-grid-col-actions pipeline-discovery-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="View Logs">
                    <Button
                      type="default"
                      size="small"
                      className="pipeline-discovery-action-btn pipeline-discovery-action-btn--logs"
                      icon={<FileTextOutlined />}
                      disabled={totalLogPaths === 0}
                      aria-label="View Logs"
                      onClick={(e) => {
                        e.stopPropagation();
                        const q = buildPipelineLogsPageParams(
                          pipeline,
                          selectedAccount?.accountId,
                        );
                        navigate(`/logs?${q.toString()}`);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="View Metrics">
                    <Button
                      type="default"
                      size="small"
                      className="pipeline-discovery-action-btn pipeline-discovery-action-btn--metrics"
                      icon={<LineChartOutlined />}
                      disabled={!pipelineSupportsMetricsPage(pipeline)}
                      aria-label="View Metrics"
                      onClick={(e) => {
                        e.stopPropagation();
                        const q = new URLSearchParams();
                        if (selectedAccount?.accountId)
                          q.set("accountId", `aws_${selectedAccount.accountId}`);
                        const typeToNamespace = {
                          glue: "Glue",
                          step_functions: "States",
                          emr: "AWS/ElasticMapReduce",
                          airflow: "MWAA",
                          mwaa: "MWAA",
                        };
                        const pipelineType =
                          pipeline.type || pipeline.pipeline_type;
                        const ns = typeToNamespace[pipelineType];
                        if (ns) {
                          q.set("metric", ns);
                        } else {
                          q.set("pipeline", pipeline.name);
                        }
                        navigate(`/metrics?${q.toString()}`);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="View Alerts">
                    <Button
                      type="default"
                      size="small"
                      className="pipeline-discovery-action-btn"
                      icon={<BellOutlined />}
                      disabled={!pipeline.pipeline_id && !pipeline.id}
                      aria-label="View Alerts"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPipelineModal(pipeline, { alertsOnly: true });
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="View Pipeline Lineage">
                    <Button
                      type="default"
                      size="small"
                      className="pipeline-discovery-action-btn pipeline-discovery-action-btn--lineage"
                      icon={<BranchesOutlined />}
                      aria-label="View Pipeline Lineage"
                      onClick={(e) => {
                        e.stopPropagation();
                        const q = new URLSearchParams();
                        if (selectedAccount?.accountId)
                          q.set("accountId", selectedAccount.accountId);
                        if (selectedAccount?.clientId)
                          q.set("clientId", selectedAccount.clientId);
                        if (pipelineId) q.set("pipelineId", pipelineId);
                        q.set("pipeline", pipeline.name);
                        q.set(
                          "pipelineType",
                          pipeline.type || pipeline.pipeline_type || "",
                        );
                        q.set(
                          "region",
                          pipeline.region || pipeline.pipeline_region || "",
                        );
                        q.set("editLineage", "1");
                        navigate(`/workflows?${q.toString()}`);
                      }}
                    />
                  </Tooltip>
                  {showSyncAction && (
                    <Tooltip title={syncTooltip}>
                      <Button
                        type="default"
                        size="small"
                        className="pipeline-discovery-action-btn pipeline-discovery-action-btn--sync"
                        icon={<SyncOutlined />}
                        aria-label="Sync pipeline onboarding"
                        loading={syncingPipelineId === pipelineId}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePipelineSync(pipeline);
                        }}
                      />
                    </Tooltip>
                  )}
                </div>

                {/* Select Checkbox */}
                <div
                  className="pipeline-discovery-grid-col-select"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      handlePipelineToggle(
                        selectionKey,
                        origIndex,
                        e.target.checked,
                      )
                    }
                  />
                </div>
              </div>
            </Card>
          );
        }) : (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Empty
              description={emptyMessage || `No ${typeDisplayName} found`}
              style={{ padding: "40px 0" }}
            />
          </div>
        )}
        </div>
      </div>
    );
  };

  // Keep original columns for potential future use (commented out)
  const getPipelineColumns = (type) => [
    {
      title: (
        <Checkbox
          checked={
            filteredPipelines[`${type}_jobs`]?.length > 0 &&
            selectedPipelines[type].size ===
              filteredPipelines[`${type}_jobs`]?.length
          }
          indeterminate={
            selectedPipelines[type].size > 0 &&
            selectedPipelines[type].size <
              (filteredPipelines[`${type}_jobs`]?.length || 0)
          }
          onChange={(e) => handleSelectAll(type)}
        />
      ),
      key: "select",
      width: 50,
      render: (_, record, index) => (
        <Checkbox
          checked={selectedPipelines[type].has(index)}
          onChange={(e) => handlePipelineToggle(type, index, e.target.checked)}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span style={{ fontWeight: 500, color: isDark ? "#e6edf3" : "#333" }}>
          {text}
        </span>
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 120,
      render: (_, record) => {
        const typeColors = {
          glue: "blue",
          step_functions: "orange",
          emr: "purple",
        };
        return (
          <Tag color={typeColors[record.type] || "default"}>
            {record.type === "glue"
              ? "Glue"
              : record.type === "step_functions"
                ? "Step Functions"
                : "EMR"}
          </Tag>
        );
      },
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 120,
    },
    {
      title: "Script Location",
      key: "script_location",
      width: 250,
      render: (_, record) => {
        if (record.type === "glue" && record.script_location) {
          return (
            <Tooltip title={record.script_location}>
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: isDark ? "#8b949e" : "#666",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record.script_location}
              </span>
            </Tooltip>
          );
        }
        return <span style={{ color: "#999", fontStyle: "italic" }}>N/A</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (_, record) => (
        <PipelineRuntimeStatus pipeline={record} compact isDark={isDark} />
      ),
    },
    {
      title: "Metrics",
      key: "metrics",
      width: 180,
      render: (_, record) => {
        const metrics = record.metrics || [];
        const metricsCount = metrics.length;
        const metricsOnboarded = metrics.filter((m) => m.onboarded).length;
        const metricsNew = metricsCount - metricsOnboarded;

        return (
          <Space size={4} direction="vertical" style={{ width: "100%" }}>
            {metricsCount > 0 ? (
              <>
                <Tag color="purple" size="small">
                  <ThunderboltOutlined /> {metricsCount} metrics
                </Tag>
                {metricsOnboarded > 0 && (
                  <Tag color="default" size="small" style={{ marginLeft: 4 }}>
                    {metricsOnboarded} onboarded
                  </Tag>
                )}
                {metricsNew > 0 && (
                  <Tag color="success" size="small" style={{ marginLeft: 4 }}>
                    {metricsNew} new
                  </Tag>
                )}
              </>
            ) : (
              <span style={{ color: "#999", fontSize: 12 }}>No metrics</span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Log Paths",
      key: "log_paths",
      width: 200,
      render: (_, record) => {
        // Handle both old format (strings) and new format (objects with path and onboarded)
        const cwPaths = record.log_paths?.cloudwatch || [];
        const s3Paths = record.log_paths?.s3 || [];

        const cwCount = cwPaths.length;
        const s3Count = s3Paths.length;

        // Count onboarded vs new
        const cwOnboarded = cwPaths.filter(
          (p) => (typeof p === "object" && p.onboarded) || false,
        ).length;
        const s3Onboarded = s3Paths.filter(
          (p) => (typeof p === "object" && p.onboarded) || false,
        ).length;

        const cwNew = cwCount - cwOnboarded;
        const s3New = s3Count - s3Onboarded;

        return (
          <Space size={4} direction="vertical" style={{ width: "100%" }}>
            {cwCount > 0 && (
              <div>
                <Tag color="blue" size="small">
                  <FileTextOutlined /> {cwCount} CW
                </Tag>
                {cwOnboarded > 0 && (
                  <Tag color="default" size="small" style={{ marginLeft: 4 }}>
                    {cwOnboarded} onboarded
                  </Tag>
                )}
                {cwNew > 0 && (
                  <Tag color="success" size="small" style={{ marginLeft: 4 }}>
                    {cwNew} new
                  </Tag>
                )}
              </div>
            )}
            {s3Count > 0 && (
              <div>
                <Tag color="green" size="small">
                  <CloudOutlined /> {s3Count} S3
                </Tag>
                {s3Onboarded > 0 && (
                  <Tag color="default" size="small" style={{ marginLeft: 4 }}>
                    {s3Onboarded} onboarded
                  </Tag>
                )}
                {s3New > 0 && (
                  <Tag color="success" size="small" style={{ marginLeft: 4 }}>
                    {s3New} new
                  </Tag>
                )}
              </div>
            )}
            {cwCount === 0 && s3Count === 0 && (
              <span style={{ color: "#999", fontSize: 12 }}>No logs</span>
            )}
          </Space>
        );
      },
    },
  ];

  const visibleLogPaths = logPathTableData.map((logPath) => ({
    type: logPath.type,
    path: logPath.path,
    onboarded: logPath.onboarded,
  }));
  const visibleSelectedLogPathsCount = visibleLogPaths.filter((logPath) =>
    selectedLogPaths[logPath.type]?.has(logPath.path),
  ).length;
  const visibleSelectedCloudWatchCount = visibleLogPaths.filter(
    (logPath) =>
      logPath.type === "cloudwatch" &&
      selectedLogPaths.cloudwatch?.has(logPath.path),
  ).length;
  const visibleSelectedS3Count = visibleLogPaths.filter(
    (logPath) =>
      logPath.type === "s3" && selectedLogPaths.s3?.has(logPath.path),
  ).length;
  const totalSelectedLogPathsCount =
    selectedLogPaths.cloudwatch.size + selectedLogPaths.s3.size;
  const hasHiddenSelectedLogPaths =
    totalSelectedLogPathsCount > visibleSelectedLogPathsCount;

  const isLogPathSelected = (record) =>
    Boolean(selectedLogPaths[record.type]?.has(record.path));

  // Log paths columns
  const logPathsColumns = [
    {
      title: (
        <Checkbox
          checked={
            visibleLogPaths.length > 0 &&
            visibleSelectedLogPathsCount === visibleLogPaths.length
          }
          indeterminate={
            visibleSelectedLogPathsCount > 0 &&
            visibleSelectedLogPathsCount < visibleLogPaths.length
          }
          onChange={(e) => {
            setSelectedLogPaths((prev) => {
              const next = {
                cloudwatch: new Set(prev.cloudwatch),
                s3: new Set(prev.s3),
              };
              if (e.target.checked) {
                visibleLogPaths.forEach((logPath) => {
                  next[logPath.type].add(logPath.path);
                });
              } else {
                visibleLogPaths.forEach((logPath) => {
                  next[logPath.type].delete(logPath.path);
                });
              }
              return next;
            });
          }}
        />
      ),
      key: "select",
      width: 50,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isLogPathSelected(record)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              handleLogPathToggle(record.type, record.path, e.target.checked)
            }
          />
        </span>
      ),
    },
    {
      title: "Pipeline",
      key: "pipeline",
      width: 260,
      render: (_, record) => {
        const typeColors = {
          glue: "blue",
          step_functions: "purple",
          emr: "orange",
          airflow: "geekblue",
          databricks: "cyan",
        };
        const typeLabels = {
          glue: "Glue",
          step_functions: "Step Functions",
          emr: "EMR",
          airflow: "Airflow",
          databricks: "Databricks",
        };
        return (
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <div style={{ fontWeight: 500, overflowWrap: "anywhere" }}>
              {record.pipelineName}
            </div>
            <Space size={4}>
              <Tag
                color={typeColors[record.pipelineType] || "default"}
                size="small"
              >
                {typeLabels[record.pipelineType] || record.pipelineType}
              </Tag>
              {record.pipelineRegion && (
                <Tag size="small">{record.pipelineRegion}</Tag>
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      render: (_, record) => (
        <Tag color={record.type === "cloudwatch" ? "blue" : "green"} size="small">
          {record.type === "cloudwatch" ? "CloudWatch" : "S3"}
        </Tag>
      ),
    },
    {
      title: "Log Path",
      dataIndex: "path",
      key: "path",
      width: 700,
      render: (path) => (
        <Tooltip title={path} placement="topLeft" overlayStyle={{ maxWidth: 560 }}>
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              color: isDark ? "#adbac7" : "#666",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.45,
              display: "block",
            }}
          >
            {path}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "onboarded",
      key: "status",
      width: 120,
      render: (onboarded) => (
        onboarded ? (
          <Tag color="default" size="small" icon={<CheckCircleOutlined />}>
            Onboarded
          </Tag>
        ) : (
          <Tag color="success" size="small">
            New
          </Tag>
        )
      ),
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1400,
        minWidth: 0,
        margin: "0 auto",
        padding: "8px 14px",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Source tabs: AWS | Databricks */}
      <Tabs
        className="page-tabs-fill"
        activeKey={activeSourceTab}
        onChange={setActiveSourceTab}
        destroyOnHidden={true}
        size="small"
        style={{ flex: 1, minHeight: 0, marginBottom: 6, display: "flex", flexDirection: "column" }}
        items={[
          {
            key: "aws",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CloudOutlined /> AWS Pipelines
              </span>
            ),
            children: (
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Header row - compact enterprise layout */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "10px",
          flexWrap: "wrap",
          gap: "8px",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Breadcrumb
          items={[{ title: "Home" }, { title: "Pipeline Discovery" }]}
        />
        <Space>
          <Tooltip title="Refresh Discovery">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleDiscoverPipelines}
              loading={discoveryLoading}
              disabled={!selectedAccount || discoveryLoading}
              size="small"
            >
              Refresh
            </Button>
          </Tooltip>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleDiscoverPipelines}
            loading={discoveryLoading}
            disabled={!selectedAccount || discoveryLoading}
            size="small"
          >
            Discover Pipelines
          </Button>
        </Space>
      </div>

      {/* Account Selection */}
      <Card
        variant="borderless"
        style={{
          marginBottom: 10,
          borderRadius: 10,
          boxShadow: isDark
            ? "0 2px 8px rgba(0,0,0,0.3)"
            : "0 2px 8px rgba(0,0,0,0.1)",
          background: isDark
            ? "rgba(22, 27, 34, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              fontWeight: 600,
              minWidth: 100,
              color: isDark ? "#e6edf3" : "#333",
              fontSize: 13,
            }}
          >
            AWS Account:
          </span>
          <Select
            style={{ flex: 1, maxWidth: 400 }}
            placeholder={
              accountsLoading
                ? "Loading accounts..."
                : "Select an AWS account to discover pipelines"
            }
            value={selectedAccount?.accountId}
            onChange={handleAccountSelect}
            allowClear
            showSearch
            size="small"
            loading={accountsLoading}
            notFoundContent={
              accountsLoading ? <Spin size="small" /> : "No accounts found"
            }
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {awsAccounts.map((account) => (
              <Option
                key={account.accountId}
                value={account.accountId}
                label={account.aws_account_name || account.accountId}
              >
                {account.aws_account_name || account.accountId} (
                {account.accountId})
              </Option>
            ))}
          </Select>
          {selectedAccount && (
            <Tag
              color="blue"
              style={{
                borderRadius: 6,
                padding: "2px 10px",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {selectedAccount.aws_account_name || selectedAccount.accountId}
            </Tag>
          )}
        </div>
      </Card>

      {/* Summary Cards - Onboarding Breakdown (same layout as Logs/Metrics) */}
      {selectedAccount && statusApiLoading && !discoveryResults && (
        <Card
          variant="borderless"
          style={{
            marginBottom: 12,
            borderRadius: 10,
            background: isDark ? "rgba(22, 27, 34, 0.85)" : "rgba(255,255,255,0.9)",
            border: isDark
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Space>
            <Spin size="small" />
            <span style={{ color: isDark ? "#adbac7" : "#666", fontSize: 13 }}>
              Loading pipeline status summary...
            </span>
          </Space>
        </Card>
      )}
      {selectedAccount && statusApiError && !discoveryResults && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Saved pipeline summary is temporarily unavailable"
          description={statusApiError}
        />
      )}
      {(discoveryResults || statusSummary) && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => selectSummaryCardFilter(ONBOARDING_FILTER_ALL)}
              style={{
                borderRadius: 10,
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.1)",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                cursor: "pointer",
              }}
              bodyStyle={{ padding: "12px 14px" }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>
                    Total Pipelines
                  </span>
                }
                value={onboardingBreakdown.total}
                valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                prefix={
                  <ThunderboltOutlined
                    style={{ color: "#fff", fontSize: 18 }}
                  />
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => selectSummaryCardFilter(ONBOARDING_FILTER_FULL)}
              style={{
                borderRadius: 10,
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.1)",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                cursor: "pointer",
              }}
              bodyStyle={{ padding: "12px 14px" }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>
                    Fully Onboarded
                  </span>
                }
                value={onboardingBreakdown.fullyOnboarded}
                valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                prefix={
                  <CheckCircleOutlined
                    style={{ color: "#fff", fontSize: 18 }}
                  />
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => selectSummaryCardFilter(ONBOARDING_FILTER_PARTIAL)}
              style={{
                borderRadius: 10,
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.1)",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                cursor: "pointer",
              }}
              bodyStyle={{ padding: "12px 14px" }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>
                    Partially Done
                  </span>
                }
                value={onboardingBreakdown.partiallyOnboarded}
                valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                prefix={
                  <CheckCircleOutlined
                    style={{ color: "#fff", fontSize: 18 }}
                  />
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              hoverable
              onClick={() => selectSummaryCardFilter(ONBOARDING_FILTER_NONE)}
              style={{
                borderRadius: 10,
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.1)",
                background: "linear-gradient(135deg, #eab308 0%, #ca8a04 100%)",
                cursor: "pointer",
              }}
              bodyStyle={{ padding: "12px 14px" }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>
                    Not Onboarded
                  </span>
                }
                value={onboardingBreakdown.notOnboarded}
                valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 700 }}
                prefix={
                  <ThunderboltOutlined
                    style={{ color: "#fff", fontSize: 18 }}
                  />
                }
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Pipeline list & filters (from discovery or status/onboarded APIs) */}
      {(discoveryResults ||
        statusSummary ||
        (onboardedPipelinesFromApi &&
          onboardedPipelinesFromApi.length > 0)) && (
        <div
          style={{
            flex: 1,
            minHeight: 420,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {selectedAccount &&
            (discoveryResults ||
              statusSummary ||
              onboardedPipelinesFromApi?.length) && (
              <Alert
                message={
                  !discoveryResults
                    ? "Quick view from onboarded data — Click Discover for full details."
                    : "Showing full discovery results — use filters below to refine."
                }
                type="info"
                showIcon
                style={{
                  marginBottom: 6,
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 11,
                }}
              />
            )}
          {/* Search and Filter */}
          <Card
            variant="borderless"
            style={{
              marginBottom: 10,
              borderRadius: 10,
              boxShadow: isDark
                ? "0 2px 8px rgba(0,0,0,0.3)"
                : "0 2px 8px rgba(0,0,0,0.1)",
              background: isDark
                ? "rgba(22, 27, 34, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.06)",
            }}
            bodyStyle={{ padding: "12px 14px" }}
          >
            <Row gutter={12} align="middle">
              <Col xs={24} sm={12}>
                <Input
                  placeholder="Search pipelines and log paths..."
                  prefix={<SearchOutlined />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  size="middle"
                  style={{ borderRadius: 8 }}
                />
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  style={{ width: "100%", borderRadius: 8 }}
                  value={filterType}
                  onChange={setFilterType}
                  size="middle"
                >
                  <Option value="all">All Types</Option>
                  <Option value="glue">Glue Jobs</Option>
                  <Option value="step_functions">Step Functions</Option>
                  <Option value="emr">EMR Clusters</Option>
                  <Option value="airflow">Airflow DAGs</Option>
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  style={{ width: "100%", borderRadius: 8 }}
                  value={logPathFilter}
                  onChange={setLogPathFilter}
                  placeholder="Filter logs"
                  size="middle"
                >
                  <Option value="all">All Logs</Option>
                  <Option value="new">New Only</Option>
                  <Option value="onboarded">Onboarded Only</Option>
                </Select>
              </Col>
              {hasActiveFilters && (
                <Col xs={24} sm={4}>
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={clearAllFilters}
                    size="large"
                    style={{ borderRadius: 8 }}
                  >
                    Clear filters
                  </Button>
                </Col>
              )}
            </Row>
          </Card>

          {/* Pipeline Status Tabs: Onboarded | Discovery | All (persisted in URL) */}
          <div
            style={{
              marginBottom: 20,
              padding: "12px 0",
              borderBottom: isDark ? "1px solid #30363d" : "1px solid #f0f0f0",
            }}
          >
            <Segmented
              value={activePipelineTab}
              onChange={(key) => {
                setOnboardingFilter(ONBOARDING_FILTER_ALL);
                setActivePipelineTab(key);
              }}
              size="large"
              options={[
                {
                  label: (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <CheckCircleOutlined />
                      Onboarded
                      <Tag color="green" style={{ margin: 0 }}>
                        {pipelineStatusCounts.onboarded}
                      </Tag>
                    </span>
                  ),
                  value: TAB_KEY_ONBOARDED,
                },
                {
                  label: (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <SearchOutlined />
                      Discovery
                      <Tag color="blue" style={{ margin: 0 }}>
                        {pipelineStatusCounts.discovery}
                      </Tag>
                    </span>
                  ),
                  value: TAB_KEY_DISCOVERY,
                },
                {
                  label: (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <ThunderboltOutlined />
                      All
                      <Tag color="default" style={{ margin: 0 }}>
                        {pipelineStatusCounts.all}
                      </Tag>
                    </span>
                  ),
                  value: TAB_KEY_ALL,
                },
              ]}
              style={{
                background: isDark ? "#161b22" : "#fafafa",
                padding: 4,
                borderRadius: 8,
              }}
            />
          </div>

          {/* Onboard Button - at top when log paths selected */}
          {summaryStats &&
            summaryStats.selectedPipelines > 0 &&
            summaryStats.selectedLogsUnique > 0 && (
            <Card
              variant="borderless"
              style={{
                marginBottom: 20,
                textAlign: "center",
                borderRadius: 12,
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.1)",
                background:
                  summaryStats.selectedLogs > 0
                    ? isDark
                      ? "linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)"
                      : "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)"
                    : isDark
                      ? "rgba(82, 196, 26, 0.12)"
                      : "rgba(82, 196, 26, 0.08)",
                border:
                  summaryStats.selectedLogs > 0
                    ? isDark
                      ? "1px solid rgba(102, 126, 234, 0.3)"
                      : "1px solid rgba(102, 126, 234, 0.2)"
                    : isDark
                      ? "1px solid rgba(82, 196, 26, 0.35)"
                      : "1px solid rgba(82, 196, 26, 0.25)",
              }}
            >
              <Space
                size="large"
                style={{ width: "100%", justifyContent: "center" }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      marginBottom: 4,
                      color: isDark ? "#e6edf3" : "#333",
                    }}
                  >
                    {summaryStats.selectedLogs > 0
                      ? "Ready to Onboard"
                      : "All Selected Logs Onboarded"}
                  </div>
                  <div
                    style={{ color: isDark ? "#8b949e" : "#666", fontSize: 14 }}
                  >
                    {summaryStats.selectedLogs > 0 ? (
                      <>
                        {summaryStats.selectedLogs} new unique log path
                        {summaryStats.selectedLogs !== 1 ? "s" : ""} to onboard
                        {summaryStats.sharedLogPathsDeduped > 0 && (
                          <Tooltip
                            title={`${summaryStats.selectedPipelineLogAssociations} log entries appear across the selected pipelines, but only ${summaryStats.selectedLogsUnique} unique paths will be sent to onboarding because shared paths are counted once.`}
                          >
                            <span style={{ marginLeft: 4, cursor: "help" }}>
                              ({summaryStats.selectedPipelineLogAssociations}{" "}
                              pipeline log entries,{" "}
                              {summaryStats.selectedLogsUnique} unique path
                              {summaryStats.selectedLogsUnique !== 1 ? "s" : ""})
                            </span>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <>
                        All {summaryStats.selectedLogsUnique} unique log path
                        {summaryStats.selectedLogsUnique !== 1 ? "s" : ""} from
                        the selected pipelines are already onboarded
                        {summaryStats.selectedPipelineLogAssociations >
                          summaryStats.selectedLogsUnique && (
                          <Tooltip
                            title={`The Log Paths tab shows ${summaryStats.selectedPipelineLogAssociations} pipeline log entries because shared paths are listed once per pipeline.`}
                          >
                            <span style={{ marginLeft: 4, cursor: "help" }}>
                              ({summaryStats.selectedPipelineLogAssociations}{" "}
                              pipeline log entries,{" "}
                              {summaryStats.selectedLogsUnique} unique path
                              {summaryStats.selectedLogsUnique !== 1 ? "s" : ""})
                            </span>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {summaryStats.selectedLogs > 0 && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={handleOnboardSelected}
                    loading={onboardingLoading}
                    disabled={
                      onboardingLoading || summaryStats.selectedLogs === 0
                    }
                    style={{
                      borderRadius: 8,
                      height: 40,
                      paddingLeft: 24,
                      paddingRight: 24,
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                    }}
                  >
                    Onboard Selected Logs
                  </Button>
                )}
              </Space>
            </Card>
          )}

          {/* Pipeline Tabs (Pipelines | Log Paths) - content filtered by status tab */}
          <Tabs
            className="page-tabs-fill pipeline-discovery-content-tabs"
            activeKey={activeAwsContentTab}
            onChange={setActiveAwsContentTab}
            destroyOnHidden={true}
            size="small"
            style={{
              flex: 1,
              minHeight: 420,
              display: "flex",
              flexDirection: "column",
            }}
            items={[
              {
                key: "pipelines",
                label: (
                  <span>
                    <ThunderboltOutlined /> Pipelines
                    {visiblePipelineTotal > 0 && (
                      <Tag style={{ marginLeft: 8 }}>{visiblePipelineTotal}</Tag>
                    )}
                  </span>
                ),
                children: (() => {
                  // Determine current list based on active pipeline type tab
                  let list = [];
                  let indices = [];
                  let page = 1;
                  let pageKey = "glue";
                  let emptyMessage = null;

                  if (activePipelineTypeTab === "stepfunctions") {
                    list = pipelinesByStatusTab.step_functions || [];
                    indices =
                      pipelinesByStatusTab.step_originalIndices || [];
                    page = pipelinePage.stepfunctions;
                    pageKey = "stepfunctions";
                    emptyMessage =
                      activePipelineTab === TAB_KEY_ONBOARDED
                        ? "No onboarded Step Functions"
                        : activePipelineTab === TAB_KEY_DISCOVERY
                          ? "No pipelines with new items"
                          : null;
                  } else if (activePipelineTypeTab === "emr") {
                    list = pipelinesByStatusTab.emr_clusters || [];
                    indices =
                      pipelinesByStatusTab.emr_originalIndices || [];
                    page = pipelinePage.emr;
                    pageKey = "emr";
                    emptyMessage =
                      activePipelineTab === TAB_KEY_ONBOARDED
                        ? "No onboarded EMR clusters"
                        : activePipelineTab === TAB_KEY_DISCOVERY
                          ? "No pipelines with new items"
                          : null;
                  } else if (activePipelineTypeTab === "airflow") {
                    const selectedEnvNameForAirflow =
                      selectedAirflowEnvironment?.name ||
                      selectedAirflowEnvironment;
                    const allAirflow = pipelinesByStatusTab.airflow_dags || [];
                    const allAirflowIndices =
                      pipelinesByStatusTab.airflow_originalIndices || [];
                    const pairedAirflow = allAirflow.map((dag, i) => ({
                      dag,
                      index: allAirflowIndices[i] ?? i,
                    }));
                    const filteredAirflow = pairedAirflow.filter(({ dag }) => {
                      if (!selectedEnvNameForAirflow) return true;
                      const dagEnv = getAirflowEnvironmentName(dag);
                      return !dagEnv || dagEnv === selectedEnvNameForAirflow;
                    });
                    list = filteredAirflow.map(({ dag }) => dag);
                    indices = filteredAirflow.map(({ index }) => index);
                    page = pipelinePage.airflow;
                    pageKey = "airflow";
                    emptyMessage =
                      activePipelineTab === TAB_KEY_ONBOARDED
                        ? "No onboarded Airflow DAGs"
                        : activePipelineTab === TAB_KEY_DISCOVERY
                          ? "No pipelines with new items"
                          : selectedEnvNameForAirflow
                            ? `No Airflow DAGs in ${selectedEnvNameForAirflow}`
                            : "No Airflow DAGs found — run Discover Pipelines";
                  } else {
                    list = pipelinesByStatusTab.glue_jobs || [];
                    indices =
                      pipelinesByStatusTab.glue_originalIndices || [];
                    page = pipelinePage.glue;
                    pageKey = "glue";
                    emptyMessage =
                      activePipelineTab === TAB_KEY_ONBOARDED
                        ? "No onboarded Glue jobs"
                        : activePipelineTab === TAB_KEY_DISCOVERY
                          ? "No pipelines with new items"
                          : null;
                  }

                  if (filterType !== "all") {
                    const typeFilteredPairs = list
                      .map((pipeline, i) => ({
                        pipeline,
                        index: indices[i] ?? i,
                      }))
                      .filter(
                        ({ pipeline }) =>
                          (pipeline.type || pipeline.pipeline_type) ===
                          filterType,
                      );
                    list = typeFilteredPairs.map(({ pipeline }) => pipeline);
                    indices = typeFilteredPairs.map(({ index }) => index);
                  }

                  const start = (page - 1) * PIPELINE_PAGE_SIZE;
                  const paginated = list.slice(
                    start,
                    start + PIPELINE_PAGE_SIZE,
                  );
                  const paginatedIndices = indices.slice(
                    start,
                    start + PIPELINE_PAGE_SIZE,
                  );
                  const selectedEnvNameForAirflow =
                    selectedAirflowEnvironment?.name ||
                    selectedAirflowEnvironment;

                  return (
                    <div
                      style={{
                        flex: 1,
                        minHeight: 360,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Type switcher (Glue | Step Functions | EMR | Airflow) */}
                      <div
                        style={{
                          flexShrink: 0,
                          marginBottom: 10,
                        }}
                      >
                        <Segmented
                          value={activePipelineTypeTab}
                          onChange={(key) => {
                            setActivePipelineTypeTab(key);
                            if (key === "glue") setFilterType("glue");
                            else if (key === "stepfunctions")
                              setFilterType("step_functions");
                            else if (key === "emr") setFilterType("emr");
                            else if (key === "airflow") setFilterType("airflow");
                          }}
                          size="small"
                          options={[
                            {
                              label: (
                                <span
                                  style={{
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <ApiOutlined style={{ fontSize: 12 }} /> Glue Jobs
                                  <Tag
                                    style={{
                                      marginLeft: 4,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {pipelinesByStatusTab.glue_jobs?.length ?? 0}
                                  </Tag>
                                </span>
                              ),
                              value: "glue",
                            },
                            {
                              label: (
                                <span
                                  style={{
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <DatabaseFilled style={{ fontSize: 12 }} /> Step Functions
                                  <Tag
                                    style={{
                                      marginLeft: 4,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {pipelinesByStatusTab.step_functions?.length ?? 0}
                                  </Tag>
                                </span>
                              ),
                              value: "stepfunctions",
                            },
                            {
                              label: (
                                <span
                                  style={{
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <CloudOutlined style={{ fontSize: 12 }} /> EMR
                                  Clusters
                                  <Tag
                                    style={{
                                      marginLeft: 4,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {pipelinesByStatusTab.emr_clusters?.length ?? 0}
                                  </Tag>
                                </span>
                              ),
                              value: "emr",
                            },
                            {
                              label: (
                                <span
                                  style={{
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <CloudServerOutlined style={{ fontSize: 12 }} /> Airflow DAGs
                                  <Tag
                                    style={{
                                      marginLeft: 4,
                                      fontSize: 11,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {visibleAirflowPipelineCount}
                                  </Tag>
                                </span>
                              ),
                              value: "airflow",
                            },
                          ]}
                          style={{
                            background: isDark ? "#161b22" : "#fafafa",
                            padding: 4,
                            borderRadius: 8,
                          }}
                        />
                      </div>

                      {/* Current pipeline type content */}
                      <div
                        style={{
                          flex: 1,
                          minHeight: 300,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {activePipelineTypeTab === "airflow" && (
                          <div
                            style={{
                              flexShrink: 0,
                              marginBottom: 10,
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Select
                              placeholder={
                                airflowEnvironmentsLoading
                                  ? "Loading environments..."
                                  : "Select Airflow environment"
                              }
                              value={selectedEnvNameForAirflow}
                              onChange={(name) => {
                                const env =
                                  airflowEnvironments.find((e) => e.name === name) ||
                                  { name };
                                setSelectedAirflowEnvironment(env);
                                setPipelinePage((prev) => ({ ...prev, airflow: 1 }));
                              }}
                              style={{ width: 280 }}
                              loading={airflowEnvironmentsLoading}
                              allowClear
                            >
                              {airflowEnvironments.map((env) => (
                                <Option key={env.name} value={env.name}>
                                  {env.name}
                                  {env.region ? ` - ${env.region}` : ""}
                                </Option>
                              ))}
                            </Select>
                            <Button
                              type="primary"
                              icon={<ThunderboltOutlined />}
                              onClick={handleDiscoverAirflowPipelines}
                              loading={airflowPipelinesLoading}
                              disabled={!selectedEnvNameForAirflow}
                            >
                              Refresh DAGs
                            </Button>
                          </div>
                        )}

                        {renderPipelineCards(
                          activePipelineTypeTab === "stepfunctions"
                            ? "step_functions"
                            : activePipelineTypeTab,
                          paginated,
                          paginatedIndices,
                          emptyMessage,
                        )}

                        {list.length > PIPELINE_PAGE_SIZE && (
                          <div
                            style={{
                              marginTop: 16,
                              display: "flex",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Pagination
                              current={page}
                              total={list.length}
                              pageSize={PIPELINE_PAGE_SIZE}
                              onChange={(p) =>
                                setPipelinePage((prev) => ({
                                  ...prev,
                                  [pageKey]: p,
                                }))
                              }
                              showSizeChanger={false}
                              showTotal={(total) => `${total} pipelines`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })(),
              },
              {
                key: "logpaths",
                label: (
                  <span>
                    <FileTextOutlined /> Log Paths
                    {visibleLogPathStats.total > 0 && (
                      <Tag style={{ marginLeft: 8 }}>
                        {visibleLogPathStats.total}
                      </Tag>
                    )}
                    {activePipelineTab === TAB_KEY_DISCOVERY &&
                      visibleLogPathStats.new > 0 && (
                        <Tag color="blue" style={{ marginLeft: 4 }}>
                          {visibleLogPathStats.new} new
                        </Tag>
                      )}
                  </span>
                ),
                children: (
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        marginBottom: 8,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 8,
                        flexShrink: 0,
                      }}
                    >
                      <Space size={6} wrap>
                        <Tag>
                          {visibleSelectedCloudWatchCount} CloudWatch selected
                        </Tag>
                        <Tag>{visibleSelectedS3Count} S3 selected</Tag>
                        {hasHiddenSelectedLogPaths && (
                          <Tag color="default">
                            {totalSelectedLogPathsCount} total selected
                          </Tag>
                        )}
                        {visibleLogPathStats.onboarded > 0 && (
                          <Tag color="default">
                            {visibleLogPathStats.onboarded} onboarded
                          </Tag>
                        )}
                        {visibleLogPathStats.new > 0 && (
                          <Tag color="success">
                            {visibleLogPathStats.new} new
                          </Tag>
                        )}
                      </Space>
                      <Space>
                        <Select
                          value={logPathFilter}
                          onChange={setLogPathFilter}
                          style={{ width: 150 }}
                        >
                          <Option value="all">All Logs</Option>
                          <Option value="new">New Only</Option>
                          <Option value="onboarded">Onboarded Only</Option>
                        </Select>
                        {activePipelineTab === TAB_KEY_DISCOVERY &&
                          (discoveryResults?.summary?.total_new || 0) > 0 && (
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleOnboardAllNew}
                              loading={onboardingLoading}
                            >
                              Onboard All New
                            </Button>
                          )}
                      </Space>
                    </div>
                    <div
                      ref={logPathsTableWrapRef}
                      className="pipeline-discovery-logpaths-table-wrap"
                    >
                      <Table
                        columns={logPathsColumns}
                        dataSource={paginatedLogPathTableData}
                        tableLayout="fixed"
                        sticky
                        scroll={{ x: 1260, y: logPathsScrollY }}
                        pagination={false}
                        locale={{
                          emptyText: <Empty description="No log paths found" />,
                        }}
                        rowKey="key"
                        rowClassName={(record) => {
                          return record.onboarded ? "log-path-row-onboarded" : "";
                        }}
                      />
                    </div>
                    {logPathTableData.length > 0 && (
                      <div className="pipeline-discovery-logpaths-pagination">
                        <Pagination
                          current={logPathsPage}
                          pageSize={logPathsPageSize}
                          total={logPathTableData.length}
                          showSizeChanger
                          pageSizeOptions={["10", "20", "50"]}
                          showTotal={(total) => `${total} log paths`}
                          onChange={(page, pageSize) => {
                            setLogPathsPage(page);
                            if (pageSize && pageSize !== logPathsPageSize) {
                              setLogPathsPageSize(pageSize);
                              setLogPathsPage(1);
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Empty State - only when no pipeline content at all */}
      {!discoveryResults &&
        selectedAccount &&
        !statusSummary &&
        (!onboardedPipelinesFromApi || onboardedPipelinesFromApi.length === 0) && (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <ThunderboltOutlined
            style={{ fontSize: 64, color: "#d9d9d9", marginBottom: 16 }}
          />
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>
            Ready to Discover Pipelines
          </div>
          <div style={{ color: "#666", marginBottom: 24 }}>
            Click "Discover Pipelines" to find Glue jobs, Step Functions, and
            EMR clusters
          </div>
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={handleDiscoverPipelines}
            loading={discoveryLoading}
          >
            Start Discovery
          </Button>
        </Card>
      )}

      {/* No Account Selected */}
      {!selectedAccount && (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <Alert
            message="Select an AWS Account"
            description="Please select an AWS account from the dropdown above to start pipeline discovery."
            type="info"
            showIcon
          />
        </Card>
      )}
              </div>
            ),
          },
          {
            key: "databricks",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <DatabaseFilled /> Databricks
              </span>
            ),
            children: (
              <DatabricksPanel
                isDark={isDark}
                tableScrollY={TABLE_SCROLL_Y}
                awsAccounts={awsAccounts}
                databricksAccounts={databricksAccounts}
                databricksAccountsLoading={databricksAccountsLoading}
                selectedDatabricksAccount={selectedDatabricksAccount}
                onSelectDatabricksAccount={(id) => {
                  const acc = databricksAccounts.find((a) => a.account_id === id);
                  setSelectedDatabricksAccount(acc || null);
                  setSelectedDatabricksWorkspace(null);
                  setDatabricksPipelines([]);
                }}
                databricksWorkspaces={databricksWorkspaces}
                selectedDatabricksWorkspace={selectedDatabricksWorkspace}
                onSelectWorkspace={(name) => {
                  const ws = databricksWorkspaces.find(
                    (w) => (w.workspace_name || w.deployment_name) === name,
                  );
                  setSelectedDatabricksWorkspace(ws || null);
                  setDatabricksPipelines([]);
                }}
                databricksPipelines={databricksPipelines}
                databricksPipelinesLoading={databricksPipelinesLoading}
                onDiscoverPipelines={handleDiscoverDatabricksPipelines}
                databricksPipelineToggling={databricksPipelineToggling}
                onTogglePipeline={handleDatabricksPipelineToggle}
              />
            ),
          },
        ]}
      />

      {/* Pipeline Detail Modal */}
      <Modal
        title={
          selectedPipelineForModal
            ? pipelineAlertsOnly
              ? `Alerts: ${selectedPipelineForModal.name}`
              : `Pipeline: ${selectedPipelineForModal.name}`
            : "Pipeline Details"
        }
        open={pipelineDetailModalOpen}
        onCancel={closePipelineDetailModal}
        footer={
          pipelineAlertsOnly
            ? [
                <Button key="close" onClick={closePipelineDetailModal}>
                  Close
                </Button>,
              ]
            : [
          selectedPipelineForModal && (
            <Button
              key="sync-pipeline"
              type="primary"
              icon={<SyncOutlined />}
              loading={
                syncingPipelineId ===
                (selectedPipelineForModal.pipeline_id ||
                  selectedPipelineForModal.id ||
                  selectedPipelineForModal.name)
              }
              onClick={() => handlePipelineSync(selectedPipelineForModal)}
            >
              Sync Pipeline
            </Button>
          ),
          selectedPipelineForModal &&
            activePipelineDetailTab === "metrics" &&
            (() => {
              const airflow = isAirflowPipeline(selectedPipelineForModal);
              const visibleMetrics = airflow
                ? filterMetricsByMwaaNamespace(
                    selectedPipelineForModal.metrics || [],
                    modalMwaaNamespaceFilter,
                  )
                : selectedPipelineForModal.metrics || [];
              const allNew = visibleMetrics.filter((m) => !m.onboarded);
              if (allNew.length === 0) return null;
              const nsLabel =
                airflow && modalMwaaNamespaceFilter !== "all"
                  ? ` (${mwaaNamespaceLabel(modalMwaaNamespaceFilter)})`
                  : "";
              return (
              <Button
                key="onboard-metrics"
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={onboardingModalMetrics}
                onClick={() => {
                  Modal.confirm({
                    title: "Onboard all new metrics?",
                    content: `Onboard ${allNew.length} metric${allNew.length !== 1 ? "s" : ""}${nsLabel} for "${selectedPipelineForModal.name}" into the Metrics configuration.`,
                    okText: "Onboard",
                    onOk: () =>
                      handleOnboardPipelineMetrics(
                        selectedPipelineForModal,
                        allNew,
                      ),
                  });
                }}
              >
                Onboard All New Metrics
              </Button>
              );
            })(),
            <Button
              key="close"
              onClick={closePipelineDetailModal}
            >
            Close
          </Button>,
        ].filter(Boolean)
        }
        width={700}
      >
        {selectedPipelineForModal && (
          <div>
            <Descriptions
              column={1}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
              labelStyle={{
                width: 160,
                minWidth: 160,
                maxWidth: 160,
                textAlign: "right",
                verticalAlign: "top",
              }}
              contentStyle={{ wordBreak: "break-word", verticalAlign: "top" }}
            >
              <Descriptions.Item label="Name">
                {selectedPipelineForModal.name}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="blue">
                  {selectedPipelineForModal.type === "glue"
                    ? "Glue"
                    : selectedPipelineForModal.type === "step_functions"
                      ? "Step Functions"
                      : selectedPipelineForModal.type === "airflow"
                        ? "Airflow"
                        : "EMR"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Region">
                {selectedPipelineForModal.region || "-"}
              </Descriptions.Item>
                  {!isGluePipeline(selectedPipelineForModal) ? (
                    <Descriptions.Item label="Status">
                      <PipelineRuntimeStatus
                        pipeline={selectedPipelineForModal}
                        isDark={isDark}
                      />
                    </Descriptions.Item>
                  ) : null}
              {isEmrPipeline(selectedPipelineForModal) && (
                <>
                  <Descriptions.Item label="Cluster ID">
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {selectedPipelineForModal.id || "-"}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {formatEmrDateTime(selectedPipelineForModal.created_on)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Elapsed time">
                    {formatEmrElapsedTime(selectedPipelineForModal)}
                  </Descriptions.Item>
                </>
              )}
              {isAirflowPipeline(selectedPipelineForModal) && (
                <>
                  <Descriptions.Item label="Environment">
                    {getAirflowEnvironmentName(selectedPipelineForModal) || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="DAG ID">
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {getAirflowDagId(selectedPipelineForModal) || "-"}
                    </span>
                  </Descriptions.Item>
                </>
              )}
              {selectedPipelineForModal.script_location && (
                <Descriptions.Item label="Script Location">
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {selectedPipelineForModal.script_location}
                  </span>
                </Descriptions.Item>
              )}
            </Descriptions>
            {pipelineAlertsOnly ? (
              <PipelineAlertsTab
                pipeline={selectedPipelineForModal}
                clientId={selectedAccount?.clientId || user?.client_id}
                awsAccountId={selectedAccount?.accountId}
              />
            ) : (
            <Tabs
              activeKey={activePipelineDetailTab}
              onChange={setActivePipelineDetailTab}
              destroyOnHidden={true}
              items={[
                {
                  key: "logs",
                  label: (
                    <span>
                      <FileTextOutlined /> Logs
                      {!pipelineDetailLoading &&
                        (() => {
                          const cw =
                            selectedPipelineForModal.log_paths?.cloudwatch ||
                            [];
                          const s3 =
                            selectedPipelineForModal.log_paths?.s3 || [];
                          const total = cw.length + s3.length;
                          if (total === 0) return null;
                          const onboarded =
                            cw.filter(
                              (p) => typeof p === "object" && p.onboarded,
                            ).length +
                            s3.filter(
                              (p) => typeof p === "object" && p.onboarded,
                            ).length;
                          const remaining = total - onboarded;
                          return (
                            <Tag style={{ marginLeft: 4 }}>
                              {onboarded}/{total}
                              {remaining > 0 ? ` (${remaining} remaining)` : ""}
                            </Tag>
                          );
                        })()}
                    </span>
                  ),
                  children: (
                    <div style={{ maxHeight: 280, overflowY: "auto" }}>
                      {pipelineDetailLoading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: 40,
                          }}
                        >
                          <Spin tip="Loading pipeline details..." />
                        </div>
                      ) : [
                        ...(selectedPipelineForModal.log_paths?.cloudwatch ||
                          []),
                        ...(selectedPipelineForModal.log_paths?.s3 || []),
                      ].length === 0 ? (
                        <Empty description="No log paths" />
                      ) : (
                        [
                          ...(
                            selectedPipelineForModal.log_paths?.cloudwatch || []
                          ).map((p, i) => ({
                            path: typeof p === "object" ? p.path : p,
                            onboarded:
                              typeof p === "object" ? p.onboarded : false,
                            type: "cloudwatch",
                            key: `cw-${i}`,
                          })),
                          ...(selectedPipelineForModal.log_paths?.s3 || []).map(
                            (p, i) => ({
                              path: typeof p === "object" ? p.path : p,
                              onboarded:
                                typeof p === "object" ? p.onboarded : false,
                              type: "s3",
                              key: `s3-${i}`,
                            }),
                          ),
                        ].map((lp) => (
                          <div
                            key={lp.key}
                            style={{
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <Tag
                              color={
                                lp.type === "cloudwatch" ? "blue" : "green"
                              }
                              size="small"
                            >
                              {lp.type === "cloudwatch" ? "CloudWatch" : "S3"}
                            </Tag>
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontSize: 12,
                                flex: 1,
                                wordBreak: "break-all",
                                minWidth: 0,
                              }}
                            >
                              {lp.path}
                            </span>
                            {lp.onboarded ? (
                              <Tag
                                color="success"
                                size="small"
                                icon={<CheckCircleOutlined />}
                              >
                                Onboarded
                              </Tag>
                            ) : (
                              <Tag color="default" size="small">
                                New
                              </Tag>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  ),
                },
                {
                  key: "metrics",
                  label: (
                    <span>
                      <ThunderboltOutlined /> Metrics
                      {!pipelineDetailLoading &&
                        (() => {
                          const metrics =
                            selectedPipelineForModal.metrics || [];
                          if (metrics.length === 0) return null;
                          const onboarded = metrics.filter((m) =>
                            Boolean(m.onboarded),
                          ).length;
                          const remaining = metrics.length - onboarded;
                          const nsSummary = isAirflowPipeline(
                            selectedPipelineForModal,
                          )
                            ? Object.entries(
                                metricsOnboardSummaryByNamespace(metrics),
                              )
                                .map(
                                  ([ns, stats]) =>
                                    `${mwaaNamespaceLabel(ns).replace(" namespace", "")} ${stats.onboarded}/${stats.total}`,
                                )
                                .join(" · ")
                            : null;
                          return (
                            <Tooltip title={nsSummary || undefined}>
                              <Tag style={{ marginLeft: 4 }}>
                                {onboarded}/{metrics.length}
                                {remaining > 0
                                  ? ` (${remaining} remaining)`
                                  : ""}
                              </Tag>
                            </Tooltip>
                          );
                        })()}
                    </span>
                  ),
                  children: (
                    <div>
                      {isAirflowPipeline(selectedPipelineForModal) && (
                        <div style={{ marginBottom: 12 }}>
                          <Segmented
                            value={modalMwaaNamespaceFilter}
                            onChange={(value) => {
                              setModalMwaaNamespaceFilter(value);
                              setModalSelectedMetricKeys(new Set());
                            }}
                            options={MWAA_METRIC_NAMESPACE_OPTIONS}
                            block
                          />
                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                            }}
                          >
                            {Object.entries(
                              metricsOnboardSummaryByNamespace(
                                selectedPipelineForModal.metrics || [],
                              ),
                            ).map(([ns, stats]) => (
                              <Tag key={ns} color="processing">
                                {mwaaNamespaceLabel(ns)}: {stats.onboarded}/
                                {stats.total}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ maxHeight: 280, overflowY: "auto" }}>
                      {pipelineDetailLoading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: 40,
                          }}
                        >
                          <Spin tip="Loading pipeline details..." />
                        </div>
                      ) : (selectedPipelineForModal.metrics || []).length ===
                        0 ? (
                        <Empty description="No metrics" />
                      ) : (
                        <>
                          {(() => {
                            const allMetrics =
                              selectedPipelineForModal.metrics || [];
                            const airflow = isAirflowPipeline(
                              selectedPipelineForModal,
                            );
                            const filteredMetrics = airflow
                              ? filterMetricsByMwaaNamespace(
                                  allMetrics,
                                  modalMwaaNamespaceFilter,
                                )
                              : allMetrics;
                            const metricGroups =
                              airflow && modalMwaaNamespaceFilter === "all"
                                ? groupMetricsByNamespace(filteredMetrics)
                                : [["", filteredMetrics]];

                            return (
                              <>
                          {filteredMetrics.some((m) => !m.onboarded) && (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                marginBottom: 10,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                size="small"
                                onClick={() => {
                                  const keys = filteredMetrics
                                    .filter((m) => !m.onboarded)
                                    .map((m) => metricSelectionKey(m));
                                  setModalSelectedMetricKeys(new Set(keys));
                                }}
                              >
                                Select all new
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  setModalSelectedMetricKeys(new Set())
                                }
                              >
                                Clear
                              </Button>
                              <Button
                                type="primary"
                                size="small"
                                icon={<ThunderboltOutlined />}
                                loading={onboardingModalMetrics}
                                disabled={modalSelectedMetricKeys.size === 0}
                                onClick={() =>
                                  handleOnboardPipelineMetrics(
                                    selectedPipelineForModal,
                                    filteredMetrics.filter((m) =>
                                      modalSelectedMetricKeys.has(
                                        metricSelectionKey(m),
                                      ),
                                    ),
                                  )
                                }
                              >
                                Onboard selected (
                                {modalSelectedMetricKeys.size})
                              </Button>
                            </div>
                          )}
                          {metricGroups.map(([namespace, groupMetrics]) => (
                            <div key={namespace || "all"}>
                              {airflow &&
                                modalMwaaNamespaceFilter === "all" &&
                                namespace && (
                                  <Divider orientation="left" plain>
                                    {mwaaNamespaceLabel(namespace)}
                                  </Divider>
                                )}
                              {groupMetrics.map((m, i) => {
                              const selKey = metricSelectionKey(m);
                              const isNew = !m.onboarded;
                              return (
                                <div
                                  key={selKey || i}
                                  style={{
                                    padding: "8px 0",
                                    borderBottom: "1px solid #f0f0f0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {isNew ? (
                                    <Checkbox
                                      checked={modalSelectedMetricKeys.has(
                                        selKey,
                                      )}
                                      onChange={(e) => {
                                        setModalSelectedMetricKeys((prev) => {
                                          const next = new Set(prev);
                                          if (e.target.checked) {
                                            next.add(selKey);
                                          } else {
                                            next.delete(selKey);
                                          }
                                          return next;
                                        });
                                      }}
                                    />
                                  ) : (
                                    <span style={{ width: 16 }} />
                                  )}
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: 12,
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {m.namespace}/{m.name}
                                  </span>
                                  {m.onboarded ? (
                                    <Tag
                                      color="success"
                                      size="small"
                                      icon={<CheckCircleOutlined />}
                                    >
                                      Onboarded
                                    </Tag>
                                  ) : (
                                    <>
                                      <Tag color="blue" size="small">
                                        New
                                      </Tag>
                                      <Button
                                        type="link"
                                        size="small"
                                        loading={onboardingModalMetrics}
                                        onClick={() =>
                                          handleOnboardPipelineMetrics(
                                            selectedPipelineForModal,
                                            [m],
                                          )
                                        }
                                      >
                                        Onboard
                                      </Button>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            </div>
                          ))}
                              </>
                            );
                          })()}
                        </>
                      )}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "lineage",
                  label: (
                    <span>
                      <LinkOutlined /> Pipeline Lineage
                    </span>
                  ),
                  children: (
                    <div style={{ padding: "8px 0" }}>
                      <Alert
                        type="info"
                        showIcon
                        message="Enable Pipeline Lineage"
                        description="When enabled, lineage tracking for this pipeline is stored in backend configuration using the pipeline key."
                        style={{ marginBottom: 16 }}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          border: "1px solid #f0f0f0",
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>Lineage Status</div>
                          <div style={{ color: "#666", fontSize: 12 }}>
                            Current setting for this pipeline
                          </div>
                        </div>
                        <Switch
                          checked={lineageEnabled}
                          onChange={setLineageEnabled}
                          checkedChildren="Enabled"
                          unCheckedChildren="Disabled"
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 16,
                        }}
                      >
                        <Button icon={<LinkOutlined />} onClick={handleEditLineageInWorkflow}>
                          Edit Lineage in Workflow Builder
                        </Button>
                        <Button
                          type="primary"
                          loading={lineageSaving}
                          onClick={handleSavePipelineLineage}
                        >
                          Save Lineage Setting
                        </Button>
                      </div>

                      <Divider style={{ margin: "16px 0 12px" }} />

                      <div style={{ marginBottom: 8, fontWeight: 600 }}>
                        Saved Workflow Lineage
                      </div>
                      <div style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
                        Lineage is read from saved Workflow Builder diagrams. Connect
                        the full chain there (for example Airflow → Glue → Step
                        Functions → EMR), then save the workflow.
                      </div>

                      {lineageViewLoading ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                          <Spin tip="Loading lineage from saved workflows..." />
                        </div>
                      ) : lineageViewError ? (
                        <Alert type="error" showIcon message={lineageViewError} />
                      ) : lineageView.matchedWorkflows === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            lineageView.scannedWorkflows > 0
                              ? "No matching lineage found in saved workflows"
                              : "No saved workflows yet"
                          }
                        />
                      ) : (
                        <div>
                          <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
                            <Tag color="blue">
                              Matched workflows: {lineageView.matchedWorkflows}
                            </Tag>
                            <Tag>Scanned workflows: {lineageView.scannedWorkflows}</Tag>
                          </Space>

                          <div style={{ marginBottom: 12 }}>
                            <div style={{ color: "#666", fontSize: 12, marginBottom: 6 }}>
                              Found in
                            </div>
                            <Space wrap size={[6, 6]}>
                              {lineageView.workflowNames.map((name) => (
                                <Tag key={name}>{name}</Tag>
                              ))}
                            </Space>
                          </div>

                          {(lineageView.paths || []).length > 0 ? (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontWeight: 500, marginBottom: 8 }}>
                                Lineage chain
                              </div>
                              {(lineageView.paths || []).map((pathItem, index) => (
                                <div
                                  key={`${pathItem.workflowName}-${index}`}
                                  style={{
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 8,
                                    padding: 10,
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ color: "#666", fontSize: 12, marginBottom: 8 }}>
                                    {pathItem.workflowName}
                                  </div>
                                  <Space wrap size={[6, 6]}>
                                    {pathItem.steps.map((step, stepIndex) => (
                                      <React.Fragment key={`${step.pipelineId || step.pipelineName}-${stepIndex}`}>
                                        {stepIndex > 0 ? (
                                          <span style={{ color: "#999" }}>→</span>
                                        ) : null}
                                        <Tag color="blue">
                                          {step.pipelineName} ({formatPipelineType(step.pipelineType)})
                                        </Tag>
                                      </React.Fragment>
                                    ))}
                                  </Space>
                                </div>
                              ))}
                            </div>
                          ) : null}

                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 500, marginBottom: 8 }}>
                              Upstream ({lineageView.upstream.length})
                            </div>
                            {lineageView.upstream.length === 0 ? (
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No upstream pipelines"
                              />
                            ) : (
                              lineageView.upstream.map((item) => (
                                <div
                                  key={`up-${item.pipelineId || item.pipelineName}-${item.region || "global"}`}
                                  style={{
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 8,
                                    padding: 10,
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ fontWeight: 500, marginBottom: 6 }}>
                                    {item.pipelineName}
                                  </div>
                                  <Space wrap size={[6, 6]}>
                                    <Tag color="blue">
                                      {formatPipelineType(item.pipelineType)}
                                    </Tag>
                                    {item.region ? <Tag>{item.region}</Tag> : null}
                                    {item.workflows.map((workflowName) => (
                                      <Tag key={`up-wf-${item.pipelineName}-${workflowName}`}>
                                        {workflowName}
                                      </Tag>
                                    ))}
                                  </Space>
                                </div>
                              ))
                            )}
                          </div>

                          <div>
                            <div style={{ fontWeight: 500, marginBottom: 8 }}>
                              Downstream ({lineageView.downstream.length})
                            </div>
                            {lineageView.downstream.length === 0 ? (
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No downstream pipelines"
                              />
                            ) : (
                              lineageView.downstream.map((item) => (
                                <div
                                  key={`down-${item.pipelineId || item.pipelineName}-${item.region || "global"}`}
                                  style={{
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 8,
                                    padding: 10,
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ fontWeight: 500, marginBottom: 6 }}>
                                    {item.pipelineName}
                                  </div>
                                  <Space wrap size={[6, 6]}>
                                    <Tag color="blue">
                                      {formatPipelineType(item.pipelineType)}
                                    </Tag>
                                    {item.region ? <Tag>{item.region}</Tag> : null}
                                    {item.workflows.map((workflowName) => (
                                      <Tag key={`down-wf-${item.pipelineName}-${workflowName}`}>
                                        {workflowName}
                                      </Tag>
                                    ))}
                                  </Space>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
            )}
          </div>
        )}
      </Modal>

      {/* Onboarding Results Modal */}
      <Modal
        title="Onboarding Results"
        open={showOnboardingResultsModal}
        onCancel={() => setShowOnboardingResultsModal(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setShowOnboardingResultsModal(false)}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {onboardingResults && (
          <div>
            {/* Summary Statistics */}
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Total Onboarded">
                <Tag
                  color="success"
                  style={{ fontSize: 14, padding: "4px 8px" }}
                >
                  {onboardingResults.summary.total_onboarded || 0}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Already Onboarded">
                <Tag
                  color="default"
                  style={{ fontSize: 14, padding: "4px 8px" }}
                >
                  {onboardingResults.summary.total_already_onboarded || 0}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Failed">
                <Tag color="error" style={{ fontSize: 14, padding: "4px 8px" }}>
                  {onboardingResults.summary.total_failed || 0}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="CloudWatch Onboarded">
                {onboardingResults.summary.cloudwatch_onboarded || 0}
              </Descriptions.Item>
              <Descriptions.Item label="S3 Onboarded">
                {onboardingResults.summary.s3_onboarded || 0}
              </Descriptions.Item>
              <Descriptions.Item label="CloudWatch Already Onboarded">
                {onboardingResults.summary.cloudwatch_already_onboarded || 0}
              </Descriptions.Item>
              <Descriptions.Item label="S3 Already Onboarded">
                {onboardingResults.summary.s3_already_onboarded || 0}
              </Descriptions.Item>
            </Descriptions>
            {(onboardingResults.summary.total_already_onboarded || 0) > 0 &&
              (onboardingResults.summary.total_onboarded || 0) === 0 &&
              (onboardingResults.summary.total_failed || 0) === 0 && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`All ${onboardingResults.summary.total_already_onboarded} selected log path${
                    onboardingResults.summary.total_already_onboarded !== 1
                      ? "s are"
                      : " is"
                  } already onboarded.`}
                />
              )}

            {/* Already Onboarded Logs */}
            {(onboardingResults.results?.cloudwatch?.already_onboarded?.length >
              0 ||
              onboardingResults.results?.s3?.already_onboarded?.length > 0) && (
              <div style={{ marginBottom: 24 }}>
                <Divider orientation="left">
                  <Tag color="default" icon={<CheckCircleOutlined />}>
                    Already Onboarded Logs
                  </Tag>
                </Divider>

                {onboardingResults.results.cloudwatch?.already_onboarded
                  ?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 8 }}>
                      CloudWatch Logs (
                      {
                        onboardingResults.results.cloudwatch.already_onboarded
                          .length
                      }
                      )
                    </h4>
                    <div
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        background: "#f5f5f5",
                        padding: 12,
                        borderRadius: 4,
                      }}
                    >
                      {onboardingResults.results.cloudwatch.already_onboarded.map(
                        (path, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom: 4,
                              fontFamily: "monospace",
                              fontSize: 12,
                            }}
                          >
                            <Tag color="default" style={{ marginBottom: 4 }}>
                              {path}
                            </Tag>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {onboardingResults.results.s3?.already_onboarded?.length >
                  0 && (
                  <div>
                    <h4 style={{ marginBottom: 8 }}>
                      S3 Logs (
                      {onboardingResults.results.s3.already_onboarded.length})
                    </h4>
                    <div
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        background: "#f5f5f5",
                        padding: 12,
                        borderRadius: 4,
                      }}
                    >
                      {onboardingResults.results.s3.already_onboarded.map(
                        (path, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom: 4,
                              fontFamily: "monospace",
                              fontSize: 12,
                            }}
                          >
                            <Tag color="default" style={{ marginBottom: 4 }}>
                              {path}
                            </Tag>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Failed Logs */}
            {(onboardingResults.results?.cloudwatch?.failed?.length > 0 ||
              onboardingResults.results?.s3?.failed?.length > 0) && (
              <div>
                <Divider orientation="left">
                  <Tag color="error" icon={<DeleteOutlined />}>
                    Failed Logs
                  </Tag>
                </Divider>

                {onboardingResults.results.cloudwatch?.failed?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 8, color: "#ff4d4f" }}>
                      CloudWatch Logs (
                      {onboardingResults.results.cloudwatch.failed.length})
                    </h4>
                    <div
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        background: "#fff1f0",
                        padding: 12,
                        borderRadius: 4,
                      }}
                    >
                      {onboardingResults.results.cloudwatch.failed.map(
                        (path, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom: 4,
                              fontFamily: "monospace",
                              fontSize: 12,
                            }}
                          >
                            <Tag color="error" style={{ marginBottom: 4 }}>
                              {path}
                            </Tag>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {onboardingResults.results.s3?.failed?.length > 0 && (
                  <div>
                    <h4 style={{ marginBottom: 8, color: "#ff4d4f" }}>
                      S3 Logs ({onboardingResults.results.s3.failed.length})
                    </h4>
                    <div
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        background: "#fff1f0",
                        padding: 12,
                        borderRadius: 4,
                      }}
                    >
                      {onboardingResults.results.s3.failed.map((path, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: 4,
                            fontFamily: "monospace",
                            fontSize: 12,
                          }}
                        >
                          <Tag color="error" style={{ marginBottom: 4 }}>
                            {path}
                          </Tag>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Successfully Onboarded (if any) */}
            {(onboardingResults.results?.cloudwatch?.onboarded?.length > 0 ||
              onboardingResults.results?.s3?.onboarded?.length > 0) && (
              <div style={{ marginTop: 24 }}>
                <Divider orientation="left">
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    Successfully Onboarded
                  </Tag>
                </Divider>
                <Alert
                  message="New Logs Onboarded"
                  description={`${onboardingResults.summary.total_onboarded} log path(s) were successfully onboarded and are now being monitored.`}
                  type="success"
                  showIcon
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PipelineDiscoveryPage;